import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'de' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved as Language) || 'de';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key;
      }
    }
    
    return typeof value === 'string' ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations: Record<Language, any> = {
  de: {
    journey: {
      aiLabel: {
        short: 'Visualisierung · KI-generiert'
      },
      hero: {
        brand: 'ENERGIEGLÜCK',
        headline: 'Großspeicher. Und die KI dahinter.',
        subline: '',
        scroll: 'Scrollen',
        swipe: 'Wischen'
      },
      problem: {
        eyebrow: 'Das Problem',
        p1: 'Anlagen werden gebaut. Und dann nur beobachtet.',
        p2: 'Dashboards zeigen. Niemand handelt.',
        p3: 'Netz, Erlös, Alterung. Drei Ziele, die sich widersprechen.'
      },
      how: {
        eyebrow: 'So funktioniert es',
        s1: {
          title: 'Reale Anlage',
          text: 'Ein Großspeicher. Entwickelt, gebaut, betrieben von Energieglück.'
        },
        s2: {
          title: 'Digital Twin',
          text: 'Physikbasiertes Abbild der Anlage. Weiß jede Sekunde, was sie tun sollte.'
        },
        s3: {
          title: 'KI-Agenten',
          text: 'Sie handeln. Am Speicher, im Netz, im Unternehmen.'
        }
      },
      landing: {
        eyebrow: 'Energieglück',
        headline: 'KI-Agenten, die handeln.',
        agents: {
          net: {
            title: 'Netz-Agent',
            text: 'Erkennt Redispatch und Anschlussgrenzen, fährt den Speicher regelkonform, dokumentiert automatisch.'
          },
          revenue: {
            title: 'Erlös-Agent',
            text: 'Rechnet Regelenergie gegen Arbitrage, zieht Alterungskosten ab, wählt den besten Fahrplan.'
          },
          state: {
            title: 'Zustands-Agent',
            text: 'Hält Reserve und SoC-Fenster ein, überwacht die Zellalterung gegen die Garantiekurve.'
          },
          forecast: {
            title: 'Prognose-Agent',
            text: 'Verbindet Wetter, Markt und Anlagenzustand zur Fahrplan-Vorschau für die nächsten 48 Stunden.'
          },
          service: {
            title: 'Service-Agent',
            text: 'Erkennt Fehler in der Anlagentopologie, öffnet das Ticket, steuert den Servicepartner.'
          },
          more: {
            title: 'Und in Ihrem Unternehmen.',
            text: 'Kundenservice, der antwortet. Dokumente, die sich selbst sortieren. Projekte, die warnen, bevor es teuer wird.',
            link: 'Mehr zu KI bei Energieglück'
          }
        }
      },
      after: {
        text: 'Energieglück entwickelt, baut und betreibt Großspeicher. Die KI, die sie steuert, haben wir selbst gebaut. Dieselben Agenten arbeiten heute für Stadtwerke und Versorger: in Prozessen, Dokumenten und Kundenkommunikation.',
        cta: 'Gespräch anfragen'
      }
    },
    nav: {
      home: 'Home',
      energieglueck: 'Energieglück+',
      batteriespeicher: 'Batteriespeicher',
      gridstable: 'GridStable',
      flaecheneigentuemer: 'Flächeneigentümer',
      netzbetreiber: 'Netzbetreiber',
      kommunen: 'Kommunen',
      company: 'Unternehmen',
      aboutUs: 'Über uns',
      about: 'Über uns',
      projects: 'Projekte & Referenzen',
      press: 'News & Presse',
      career: 'Karriere',
      imprint: 'Impressum',
      contact: 'Kontakt',
      playground: 'KI Live Demo',
      knowledge: 'Wissen',
      whitepaper: 'Whitepaper',
      useCases: 'Use Cases'
    },
    plattform: {
      nav: {
        cta: "Demo buchen",
        dd: {
          uebersicht: "Übersicht",
          anwendungen: "Anwendungen",
          wirtschaftlichkeit: "Wirtschaftlichkeit",
          referenzen: "Referenzen",
        },
      },
      hero: {
        eyebrow: "KI-Agenten + Digital Twin für PV & Batteriespeicher",
        h1: "Kein Dashboard. KI-Agenten, die Ihre Anlage managen.",
        lede: "Energieglück+ ist ein selbstlernender, physikbasierter digitaler Zwilling Ihrer PV- und Batteriespeicher-Anlagen — mit KI-Agenten, die Ertrag, Degradation, Sicherheit und Vermarktung autonom überwachen. Sie sehen die Ergebnisse, nicht das Dashboard.",
        cta1: "Demo buchen",
        cta2: "Wirtschaftlichkeit ansehen",
        trustLine: "DSGVO-konform · Made in Germany · Hosting Deutschland",
      },
      kpi: {
        k1: "96 %",
        l1: "Prognosegüte unserer KI-Agenten im Betrieb",
        note: "Gehaltener Wert im Betrieb, Methodik je Fallstudie — kein pauschaler Marketing-Wert.",
      },
      uebersicht: {
        intro: {
          eyebrow: "Was unsere KI-Agenten leisten",
          h2: "Ein Twin Ihrer Anlage. Agenten, die ihn managen.",
          p: "Der digitale Zwilling bildet jede PV- und Speicheranlage physikbasiert ab — bis auf String- und Zell-Ebene. Darauf arbeiten KI-Agenten: Ertrags-, Diagnose-, Sicherheits- und Vermarktungs-Agent. Vom klassischen Monitoring (Essentials) über die Agenten-Welt (Pro) bis zur vollständigen Betriebsführung durch das Energieglück-Team (Full-Service).",
        },
        monitoring: {
          t: "Agenten statt Dashboards",
          p: "Ertrags-, Degradations-, Sicherheits- und Vermarktungs-Agent arbeiten 24/7 auf dem digitalen Zwilling Ihrer Anlage — im 15-Minuten-Takt, mit konkreten Handlungsempfehlungen statt bunter Kacheln.",
        },
        teaser: {
          cta: "Mehr erfahren",
        },
        finalCta: {
          title: "Sehen Sie, was in Ihrem Portfolio steckt.",
        },
      },
      anwendungen: {
        eyebrow: "Anwendungen",
        h2: "PV-Anlagen und Batteriespeicher in einer Plattform.",
        hero: {
          subtitle: "Ein digitaler Zwilling für beide Anlagentypen — vom etablierten PV-Monitoring bis zur Batteriespeicher-Komponente mit eigener Entscheidungsschicht. KI-Prognose statt reiner Anzeige.",
        },
        section: {
          h2: "Zwei Welten. Eine Plattform.",
        },
        forest: {
          h2: "PV und Speicher unter einer Logik. Agenten, die handeln.",
          p: "Der digitale Zwilling bildet PV-Anlagen und Batteriespeicher physikbasiert ab. KI-Agenten arbeiten darauf — proaktive Wartung, Ertragsoptimierung und Vermarktungs-Entscheidungen statt reaktiver Störungsmeldung.",
        },
        pv: {
          t: "PV-Anlagen",
          p: "Der etablierte digitale PV-Zwilling verbindet eine 15-Minuten-Zeitreihe der Ist-Daten mit einer Bewertungslogik und macht so Verluste sichtbar, die im Standard-Monitoring unsichtbar bleiben.",
          lead: "Ertrags-Agent + Diagnose-Agent — Verluste sichtbar machen, die das Standard-Monitoring übersieht.",
          c1: "Ertrags-Agent",
          c2: "Diagnose-Agent",
          c3: "Verdeckte Verluste sichtbar",
          c4: "API-/Datenlogger-Anbindung",
          b1: "Digitaler Zwilling mit KI-Prognose: PV-Forecast und PV-Sollwert, Wetterdienst integriert.",
          b2: "Proaktive Wartung und Fehlerdiagnose statt reaktiver Störungsmeldung.",
          b3: "Macht verdeckte Verluste sichtbar: DC/AC-Überbelegung, MPP-Tracking, Verschattung, thermische Verluste — ohne pauschale Geldbeträge.",
          b4: "Wechselrichter-Anbindung über API oder Datenlogger.",
        },
        bess: {
          t: "Batteriespeicher (BESS)",
          p: "Der Batteriespeicher-Zwilling rechnet physikbasiert: Restlebensdauer, herstellerunabhängiger SOH, Sicherheit und eine Entscheidungsschicht für regulatorisch und kaufmännisch relevante Situationen.",
          lead: "Sicherheits-, Degradations- und Vermarktungs-Agent — Lebensdauer schützen, Erlöse sichern, Sicherheit nachweisen.",
          c1: "Degradations-Agent (SOH/RUL)",
          c2: "Sicherheits-Agent (IEC 62619)",
          c3: "Selbstlernender Zwilling",
          c4: "Vermarktungs-Agent",
          b1: "Restlebensdauer-Prognose (RUL): verbleibende Nutzungsdauer je Rack und Standort mit Konfidenzband und End-of-Life-Datum.",
          b2: "Herstellerunabhängiges SOH-Modell: State of Health selbst berechnet statt aus dem BMS übernommen — objektiv, mit Garantie-Tracking.",
          b3: "Thermal-Runaway-Überwachung nach IEC 62619: sicherheitsrelevante Ereignisse aggregiert und priorisiert, mit Audit-Trail.",
          b4: "Selbstlernender digitaler Zwilling: lernt das reale Zellverhalten Ihrer Anlage und kalibriert sich gegen Drift.",
          b5: "Anomalieerkennung in Echtzeit: Abweichungen Modell↔Messung werden statistisch erkannt, bevor sie zum Ausfall führen.",
          b6: "Entscheidungsschicht: konkrete Handlungsempfehlung bei Redispatch / §13a EnWG, negativen Preisen, PCC-Limit und FCR-Konflikten.",
          b7: "Garantie- und Zyklen-Budget: Zyklenverbrauch gegen den Garantievertrag projiziert — Vertragsbruch-Risiko früh sichtbar.",
          b8: "P&L je Anlage: Erlöse (Arbitrage / FCR / aFRR / Redispatch) gegen Degradations-, HVAC- und Opportunitätskosten — Soll/Ist je Monat.",
        },
        tech: {
          t: "Technologie",
          p: "Kein generisches Dashboard: Energieglück+ rechnet mit einem selbstlernenden, physikbasierten digitalen Zwilling für PV-Anlagen und Batteriespeicher — für Speicher zusätzlich Restlebensdauer- und SOH-Prognose mit Konfidenzband, Arrhenius-Degradationsmodell, IEC-62619-Sicherheitsüberwachung und statistische Anomalieerkennung; für PV Forecast und verdeckte Ertragsverluste. Das ist die Logik führender Asset-Management-Forschung, produktiv im Betrieb. Datenquellen SCADA, Datenlogger oder API im 15-Minuten-Takt; Hosting in Deutschland beziehungsweise der EU.",
        },
        andock: {
          eyebrow: "Anbindung",
          h2: "Ihre Hardware bleibt. Unsere KI dockt an.",
          c1: {
            t: "Keine neue Hardware",
            p: "Kein CapEx, kein Vor-Ort-Besuch. Wir lesen aus, was bei Ihnen schon läuft — bei PV-Wechselrichtern ebenso wie bei Speicher-BMS. Sie bauen nichts um.",
          },
          c2: {
            t: "Herstellerunabhängig",
            p: "Anbindung an FusionSolar, SMA, Fronius, Sungrow, Meteocontrol und MQTT. Den Speicher-SOH rechnen wir physikalisch selbst — nicht aus dem BMS übernommen.",
          },
          c3: {
            t: "In Minuten live, nicht Monaten",
            p: "Es genügen die Zugangsdaten für Wechselrichter-API, BMS oder SCADA. KI-gestütztes Self-Onboarding bringt PV- und Speicheranlagen in der Regel innerhalb weniger Minuten produktiv.",
          },
        },
        finalCta: {
          title: "Bereit für messbare Betriebsführung?",
        },
      },
      wirtschaft: {
        eyebrow: "Wirtschaftlichkeit",
        h2: "Wir rechnen mit Physik, nicht mit Versprechen.",
        hero: {
          subtitle: "Verdeckte Verluste werden sichtbar, abgerechnet wird nach der Formel im Kundenvertrag — keine generische Standard-KPI, keine pauschalen Versprechen.",
        },
        value: {
          t: "Wie Energieglück+ Wert schafft",
          p: "Monitoring zeigt Zustände — KI-Agenten handeln, der digitale Zwilling macht es nachvollziehbar. Essentials liefert das klassische Monitoring. Pro startet die Agenten-Welt auf dem digitalen Zwilling: Ertrags-, Diagnose-, Sicherheits- und Entscheidungs-Agent prognostizieren Restlebensdauer, SOH und Performance mit Konfidenzband und rechnen alles in Euro um. Full-Service ergänzt Vermarktungs- und Wartungs-Agent — vom Energieglück-Team überwacht, abgerechnet exakt nach der Formel in Ihrem Vertrag.",
        },
        tiers: {
          h2: "Drei Stufen, je nach Betriebsmodell.",
          anchorBadge: "ANCHOR",
          essentials: {
            t: "Essentials",
            for: "Einfaches Monitoring.",
            i1: "Monitoring",
            i2: "Alerting",
            i3: "Standard-Reports",
            i4: "",
            i5: "",
            i6: "",
            footLabel: "",
            foot: "",
          },
          pro: {
            t: "Pro",
            for: "Erweitertes Monitoring.",
            i1: "Alles aus Essentials",
            i2: "KI-Agenten",
            i3: "Forecast",
            i4: "Digitaler Zwilling",
            i5: "Predictive Maintenance",
            i6: "Audit-Export",
            footLabel: "",
            foot: "",
          },
          fullservice: {
            t: "Full-Service",
            for: "Asset-Management-as-a-Service — Plattform + menschliche Betreuung durch Asset Manager.",
            i1: "Alles aus Pro",
            i2: "Menschliche Betreuung durch Asset Manager",
            i3: "Kaufmännisches Asset-Management",
            i4: "Investoren-Reporting",
            i5: "",
            i6: "",
            footLabel: "HINWEIS",
            foot: "Kein On-Site O&M — ausschließlich über Partner.",
          },
          volumeLabel: "MENGENSTAFFELN",
          volume: "Definierte Rabattstufen entlang der Wachstumskurve — sprechen Sie uns für die Staffelpreise an.",
        },
        versicherung: {
          t: "Versicherung",
          p: "In Partnerschaft mit Versicherern: dokumentierte Risk-Reduction als Verhandlungsgrundlage. Kein Versprechen zur Prämiensenkung.",
        },
        foerderung: {
          head: "Ihre Investition wird gefördert",
          sub: "Zahlreiche Bundes- und Landesprogramme unterstützen die Digitalisierung von Energieanlagen.",
          note: "Förderhöhe abhängig von Standort, Unternehmensgröße und Programmverfügbarkeit.",
          rateLabel: "Förderung",
          maxLabel: "Max.",
          p1: { name: "InnoBonus", region: "Saarland", rate: "bis 75 %", max: "75.000 €" },
          p2: { name: "Digitalbonus", region: "Bayern", rate: "bis 50 %", max: "30.000 €" },
          p3: { name: "DIGI-Zuschuss", region: "Hessen", rate: "50 %", max: "10.000 €" },
          p4: { name: "KfW ERP-Kredit", region: "Bundesweit", rate: "3 % Tilgung", max: "200.000 €" },
        },
        finalCta: {
          title: "Lassen Sie uns über Ihr Portfolio sprechen.",
        },
      },
      ref: {
        eyebrow: "Referenzen",
        h2: "Im laufenden Betrieb, real.",
        hero: {
          subtitle: "Stadtwerke, PV-Portfolios und Betreiber, bei denen unsere KI-Agenten auf dem digitalen Zwilling produktiv laufen — keine Pilot-Folien, sondern Anlagen im täglichen Einsatz.",
        },
        section: {
          h2: "Kunden, die mit uns arbeiten.",
        },
        finalCta: {
          title: "Werden Sie die nächste Referenz.",
        },
        goettingen: {
          name: "Stadtwerke Göttingen",
          desc: "Kommunaler PV-Betrieb.",
          quote: "Die Zusammenarbeit mit der Energieglück AI GmbH und der Einsatz von Energieglück+ ist für die Stadtwerke Göttingen durchweg positiv. Die Plattform ermöglicht uns ein intelligentes, effizientes Anlagenmanagement.",
          person: "Bernd Schill",
          role: "Leiter Erneuerbare Energien, Stadtwerke Göttingen",
        },
        aream: {
          name: "Aream",
          desc: "PV-Portfolio in Deutschland, Italien und Spanien.",
        },
        green4net: {
          name: "Green4Net",
          desc: "PV-Betrieb in Italien und Deutschland.",
        },
        trust: {
          dsgvo: "DSGVO-konform",
          madeingermany: "Made in Germany",
          hosting: "Hosting Deutschland",
        },
      },
      icpfaq: {
        investoren: "Investoren: due-diligence-fähige Ertrags- und Risiko-Transparenz.",
        stadtwerke: "Stadtwerke: regulatorisch sauberer kommunaler Betrieb von PV und Speicher.",
        projektierer: "Projektierer: nahtloser Übergang vom Bau in den optimierten Betrieb.",
        faq: {
          q1: "Welche Anlagen unterstützt Energieglück+?",
          a1: "PV- und Batteriespeicher-Anlagen im Utility-Scale ab 5 MWh, mit einem digitalen Zwilling bis auf Zell-Ebene.",
          q2: "Wie kommen die Daten in die Plattform?",
          a2: "Über SCADA, Datenlogger oder API im 15-Minuten-Takt; die Wechselrichter-Anbindung erfolgt über API oder Datenlogger.",
          q3: "Wo werden die Daten gehostet?",
          a3: "DSGVO-konform in Deutschland beziehungsweise der EU. Made in Germany, Hosting Deutschland.",
          q4: "Wer übernimmt das Onboarding?",
          a4: "In Essentials und Pro führt die Plattform Sie KI-gestützt durch das Self-Onboarding — es genügen die Zugangsdaten für BMS, SCADA oder API; auf Wunsch begleiten wir Sie dabei. Full-Service übernimmt das Energieglück-Team vollständig inklusive vertragsgenauer KPI-Abrechnung.",
          q5: "Wie schnell bin ich live?",
          a5: "Self-Onboarding ist in der Regel innerhalb weniger Minuten abgeschlossen, sobald die Zugangsdaten vorliegen.",
          q6: "Was unterscheidet euch von reinem Monitoring?",
          a6: "Monitoring zeigt Zustände. Energieglück+ rechnet mit einem physikbasierten digitalen Zwilling und betreibt darauf KI-Agenten, die prognostizieren und konkrete Handlungsempfehlungen bis zur Betriebsführung geben.",
          q7: "Wie berechnet ihr den State of Health (SOH)?",
          a7: "Herstellerunabhängig und selbst berechnet — objektiv statt aus dem BMS übernommen; inklusive Garantie-Tracking.",
          q8: "Kann ich später die Stufe wechseln?",
          a8: "Ja. Essentials → Pro → Full-Service ist jederzeit möglich, ohne Datenmigration.",
          q9: "Was passiert mit meinen Daten bei Kündigung?",
          a9: "Vollständiger Datenexport, keine Lock-in-Mechanismen.",
          q10: "Unterstützt ihr Multi-Anlagen-Portfolios verschiedener Betreiber?",
          a10: "Ja, mandantengetrennt mit strikter Tenant-Isolation.",
        },
      },
      faq: {
        eyebrow: "Häufige Fragen",
        h2: "Antworten auf einen Blick.",
      },
    },
    hero: {
      slide1: {
        badge: 'KI Asset Management',
        subtitle: 'KI-Agenten managen Ihre PV- und Batteriespeicher-Anlagen auf einem selbstlernenden, physikbasierten digitalen Zwilling.',
        feature1: 'Digitaler Zwilling',
        feature2: 'KI-gestützte Prozessautomatisierung',
        feature3: 'Day-Ahead Forecasting',
        feature4: 'Predictive Maintenance',
        feature5: 'Echtzeit-Monitoring & Optimierung',
        cta: 'Zum Produkt'
      },
      slide2: {
        title: 'Batteriespeicher als Netzhelfer',
        subtitle: 'Mit GridStable transformieren wir Batteriespeicher von reinen Gewinnmaximierern zu aktiven Netzhelfern – für eine stabile und profitable Energiezukunft.',
        pipeline: '2GW+ Pipeline',
        development: 'Entwicklung, Bau & Betrieb',
        cta1: 'GridStable entdecken',
        cta2: 'Projekte ansehen'
      },
      slide3: {
        badge: 'NEU',
        title: 'Ihre Kunden warten. Ihre KI antwortet.',
        subtitle: 'Während andere noch E-Mails sortieren, hat Ihre KI schon geantwortet, den Kunden erkannt und das Problem gelöst.',
        cta: 'KI entdecken'
      },
      stats: {
        pipeline: 'Batteriespeicher',
        technology: 'Technologie',
        ai: 'KI Asset Management'
      }
    },
    home: {
      what: {
        eyebrow: 'Was wir machen',
        title: 'Drei Dinge. KI ist die Basis von allem.',
        card1: {
          eyebrow: 'KI — unsere DNA',
          title: 'Eigene Modelle, Agenten & Automatisierung',
          desc: 'Von E-Mail-Klassifikation über OCR bis Intent-Erkennung — KI durchzieht alles, was wir tun.',
          cta: 'KI-Services'
        },
        card2: {
          eyebrow: 'Projektentwicklung',
          title: 'Großbatteriespeicher',
          desc: 'BESS von 5 bis 700 MW in Deutschland — KI-gestützt vom Scouting bis RTB.',
          cta: 'GridStable™'
        },
        card3: {
          eyebrow: 'Energieglück+',
          title: 'KI-Agenten + Digital Twin für PV & BESS',
          desc: 'Ertrags-, Diagnose-, Sicherheits- und Vermarktungs-Agenten arbeiten auf einem physikbasierten Zwilling Ihrer Anlage — statt bunter Dashboards.',
          cta: 'Demo anfragen'
        }
      },
      who: {
        eyebrow: 'Mit wem wir arbeiten',
        title: 'Stadtwerke, Netzbetreiber, IPPs — und die Menschen dahinter.',
        card1: {
          title: 'Flächeneigentümer',
          kpiValue: '20+',
          kpiLabel: 'Jahre Pacht',
          desc: 'Langfristige, planbare Einnahmen auf Flächen ab ca. 1 Hektar — keine Kosten, keine Risiken.',
          cta: 'Mehr erfahren'
        },
        card2: {
          title: 'Netzbetreiber',
          kpiValue: '24/7',
          kpiLabel: 'Netzstabilisierung',
          desc: 'Integration in bestehende Netzinfrastruktur. Erfüllt VDE-AR-N, TAR und EEG — sofort einsatzbereit.',
          cta: 'Mehr erfahren'
        },
        card3: {
          title: 'Kommunen',
          kpiValue: '90%',
          kpiLabel: 'Gewerbesteuer lokal',
          desc: 'Seit Jahressteuergesetz 2024 verbleiben 90 % der Gewerbesteuer vor Ort. Vorreiter-Rolle in der Energiewende.',
          cta: 'Mehr erfahren'
        }
      },
      join: {
        title: 'Teil der Energiewende werden.',
        subtitle: 'Ob Fläche, Netz, Kommune oder Industriepartner — sprechen wir darüber, wie wir gemeinsam Energie sinnvoll machen.',
        ctaPrimary: 'Kontakt aufnehmen',
        ctaSecondary: 'Über uns',
        trust: 'Antwort innerhalb von 24 h · info@energieglueck.de'
      }
    },
    index: {
      valueTitle: 'Ihr Mehrwert mit Energieglück',
      valueSubtitle: 'Wir schaffen echten Mehrwert für unsere Partner – mit innovativen Lösungen für Batteriespeicher und PV-Anlagen',
      operators: {
        title: 'Für Parkbetreiber',
        subtitle: 'Energieglück+ steigert Ihre Anlagenperformance',
        feature1: 'KI-gestütztes Asset Management mit digitalem Zwilling',
        feature2: 'Predictive Maintenance reduziert Ausfallzeiten',
        feature3: 'Echtzeit-Monitoring & Day-Ahead Forecasting',
        feature4: 'Maximale Erträge durch KI-Optimierung',
        cta: 'Energieglück+ entdecken'
      },
      landowners: {
        title: 'Für Flächeneigentümer',
        subtitle: 'Langfristige Pachteinnahmen ohne Eigeninvestition',
        feature1: '20+ Jahre garantierte Pachteinnahmen',
        feature2: 'Keine Kosten oder Risiken für Sie',
        feature3: 'Wir übernehmen Entwicklung, Bau & Betrieb',
        feature4: 'Beitrag zur regionalen Energiewende',
        cta: 'Mehr erfahren'
      },
      municipalities: {
        title: 'Für Kommunen',
        subtitle: 'Gewerbesteuer, Klimaziele & zukunftsfähiger Energiestandort',
        feature1: 'Hohe Gewerbesteuereinnahmen (90% bleiben lokal)',
        feature2: 'Klimaziele erreichen & Vorreiterrolle einnehmen',
        feature3: 'Basis für E-Mobilität & Wärmenetze',
        feature4: '100% private Investition – kein kommunales Risiko',
        cta: 'Mehr erfahren'
      },
      companies: {
        title: 'Für Unternehmen',
        subtitle: 'KI-Lösungen die Ihr Team entlasten und Kunden begeistern',
        feature1: 'Reachy — KI-Mitarbeiter für alle Kanäle, 24/7',
        feature2: '10+ sofort einsetzbare Use Cases',
        feature3: '60-90% schnellere Kundenbearbeitung',
        feature4: 'DSGVO-konform, Made in Germany',
        cta: 'Use Cases entdecken'
      },
      group: {
        title: 'Die Energieglück Gruppe',
        subtitle: 'Zwei Unternehmen, eine Mission: Die Energiewende intelligent gestalten.',
        bess: {
          name: 'Batteriespeicher',
          legal: 'Energieglück Batteriespeicher GmbH',
          feature1: 'GridStable — Netzstabilisierung & Regelenergie',
          feature2: 'Co-Location & Umspannwerke',
          feature3: '2GW+ Entwicklungspipeline',
          feature4: 'Betrieb & Asset Management',
          cta: 'GridStable entdecken'
        },
        ai: {
          name: 'Künstliche Intelligenz',
          legal: 'Energieglück AI GmbH',
          feature1: 'Energieglück+ — KI Asset Management',
          feature2: 'KI-Agenten für Geschäftsprozesse',
          feature3: 'KI-Beratung & Digitalisierung',
          feature4: 'Unternehmensberatung KI-Strategie',
          cta: 'KI entdecken'
        },
        claim: 'Wir bauen die Speicher. Und die Intelligenz dahinter.',
        trust: {
          title: 'Partner aus der Energiewirtschaft vertrauen auf uns',
          text: 'u.a. Stadtwerke Göttingen AG'
        }
      },
      closing: 'Als Projektentwickler realisieren wir gemeinsam mit starken Partnern Batteriespeicher-Projekte von der Planung bis zum Betrieb. Mit Energieglück+ bieten wir zusätzlich eine KI-gestützte Asset-Management-Plattform — autonome KI-Agenten auf einem digitalen Zwilling Ihrer Anlage.',
      tech: {
        title: 'Innovation durch Technologie',
        gridstable: {
          title: 'GridStable - Intelligente Netzstabilisierung',
          feature1: 'Frequenzstabilität durch BESS',
          feature2: 'Spannungshaltung',
          feature3: 'Systemdienstleistungen',
          feature4: 'Blackstart-Fähigkeit'
        },
        energieglueck: {
          title: 'Energieglück+ - KI Asset Management',
          feature1: 'Digitaler Zwilling für Energieanlagen',
          feature2: 'KI-gestützte Prozessautomatisierung',
          feature3: 'Echtzeit-Monitoring & Forecasting',
          feature4: 'Predictive Maintenance'
        },
        reachy: {
          title: 'Reachy - KI-Assistent für Unternehmen',
          feature1: 'Automatisierte Kundenkommunikation (E-Mail, Chat, Telefon)',
          feature2: 'Multi-Agenten-Teams für komplexe Aufgaben',
          feature3: 'Proaktives Arbeiten — erkennt Handlungsbedarf eigenständig',
          feature4: 'DSGVO-konform, Made in Germany'
        },
        cta1: 'GridStable entdecken',
        cta2: 'Energieglück+ kennenlernen',
        cta3: 'Reachy kennenlernen'
      },
      cta: {
        title: 'Teil der Energiewende werden',
        subtitle: 'Lassen Sie uns gemeinsam die Energiezukunft gestalten',
        offer: {
          title: 'Fläche anbieten',
          text: 'Profitieren Sie von langfristigen Pachteinnahmen',
          cta: 'Mehr erfahren'
        },
        project: {
          title: 'Projekt besprechen',
          text: 'Für Stadtwerke, Kommunen und Projektentwickler — gemeinsam planen',
          cta: 'Gespräch vereinbaren'
        },
        ki: {
          title: 'KI-Lösungen entdecken',
          text: 'Beratung, Software und KI-Agenten für Ihr Unternehmen',
          cta: 'KI-Angebot ansehen'
        }
      }
    },
    assetMgmt: {
      meta: {
        title: 'KI Asset Management für PV & Batteriespeicher | Energieglück+',
        description: 'Digitaler Zwilling, Prädiktive Wartung und Echtzeit-Steuerung für PV- und BESS-Portfolios. +15% Ertragsgewinn, -40% Ausfallzeiten.'
      },
      hero: {
        title: 'Kein Brandrisiko. Keine Überraschungen. Keine Ertragsausfälle.',
        subtitle: 'KI Asset Management für PV- und BESS-Portfolios.'
      },
      stats: {
        yield: 'Ertragsgewinn',
        downtime: 'Ausfallzeiten',
        intraday: 'Intraday-Profit'
      },
      areas: {
        title: 'Zwei Welten. Eine Plattform.',
        moreLink: 'Mehr auf Energieglück+',
        pv: {
          title: 'PV-Anlagen',
          f1: 'Digitaler Zwilling mit physikalischer Sollwertberechnung',
          f2: 'KI-Prognose mit 96% Genauigkeit',
          f3: 'Proaktive Wartung vor sichtbaren Ausfällen',
          f4: 'Monetäre Verlustbewertung in Echtzeit',
          f5: 'Redispatch-Optimierung und Intraday-Handel'
        },
        bess: {
          title: 'Batteriespeicher',
          f0: 'Digitaler Zwilling für BESS — vom Rack bis zur Zelle',
          f1: 'SOH- und RUL-Überwachung auf Zellebene',
          f2: 'Thermische Früherkennung und SOC-Optimierung',
          f3: 'SOC-Drift-Korrektur und Degradationsanalyse',
          f4: 'Versicherungsrabatt für BESS-Betreiber'
        }
      },
      features: {
        title: 'Features',
        card1: { title: 'Vorausschauende Fehlerdiagnose', text: 'KI erkennt Anomalien auf allen Topologieebenen — bevor sie zu Ausfällen werden.' },
        card2: { title: 'Intelligentes Redispatch-Management', text: 'Automatisierte Einsatzplanung und Netzengpassmanagement für PV und BESS.' },
        card3: { title: 'Prädiktive Wartung', text: 'Datengetriebene Wartungsplanung senkt Kosten und maximiert Verfügbarkeit.' },
        card4: { title: 'Echtzeit-Steuerung', text: 'Direkter Zugriff auf Wechselrichter und BMS — Eingriffe in Sekunden, nicht Stunden.' }
      },
      usp: {
        title: '24/7 KI-Überwachung + Persönliche Betreuung',
        subtitle: 'Automatisierung, wo sie sinnvoll ist. Erfahrene Asset Manager, wenn es drauf ankommt. Ihre persönliche Leitwarte.'
      },
      caseStudy: {
        title: '260+ MWp analysiert. 1,5 Mio € Verluste pro Jahr gefunden.',
        subtitle: 'Unsere KI hat in bestehenden Portfolios systematisch versteckte Ertragseinbußen identifiziert — von Verschattung über Degradation bis zu fehlerhaften Stringkonfigurationen.'
      },
      cta: {
        title: 'Sehen Sie selbst, was in Ihrem Portfolio steckt.',
        button: 'Demo buchen'
      }
    },
    projektmgmt: {
      meta: {
        title: 'KI-Projektmanagement für Energieprojekte | Energieglück',
        description: 'KI über alle Projektunterlagen, Frühwarnsystem für Risiken und ein System über den gesamten Lebenszyklus — von Scouting bis Betrieb. Bewährt am größten Speicherprojekt Deutschlands.'
      },
      hero: {
        eyebrow: 'KI-Projektmanagement',
        title: 'Projektmanagement, das mitdenkt.',
        subtitle: 'KI über alle Projektunterlagen, Frühwarnsystem für Risiken — bewährt am größten Speicherprojekt Deutschlands.',
        cta1: 'Demo anfragen',
        cta2: 'Mehr KI-Lösungen'
      },
      areas: {
        eyebrow: 'Was es leistet',
        title: 'Drei Bausteine für Energieprojekte.',
        rag: {
          title: 'KI über alle Projektunterlagen',
          f1: 'Fragen in natürlicher Sprache an Verträge, Gutachten, Protokolle und Behördenkorrespondenz.',
          f2: 'Automatischer Dokumenten-Ingest aus SharePoint, Paperless und Meeting-Mitschriften.',
          f3: 'Antworten mit Quellenbezug — kein Suchen in Ordnerstrukturen.'
        },
        warn: {
          title: 'Frühwarnsystem für Projektrisiken',
          f1: 'Fristen, Genehmigungs- und Netzanschluss-Risiken werden proaktiv erkannt.',
          f2: 'Statt reaktiver Statusmeldung: Hinweis, bevor ein Engpass eskaliert.',
          f3: 'Priorisierte Risiko-Übersicht über das gesamte Projektportfolio.'
        },
        lifecycle: {
          title: 'Vom Scouting bis zum Betrieb',
          f1: 'Ein System über den gesamten Lebenszyklus: Akquise, aktive Projektphase, Abschluss.',
          f2: 'Aufgaben, Checklisten-Templates und zentrale Kontaktdatenbank (Netzbetreiber, Behörden, Eigentümer).',
          f3: 'Öffentliche Projekt-Statusseite für transparente Stakeholder-Kommunikation.'
        }
      },
      caps: {
        eyebrow: 'Unter der Oberfläche',
        title: 'KI-nativ gebaut.',
        c1: { title: 'Dokumenten-RAG', text: 'Retrieval-Augmented Generation über die komplette Projektablage — mit Quellennachweis.' },
        c2: { title: 'Frühwarn-Engine', text: 'Regel- und datenbasierte Risikoerkennung über alle laufenden Projekte.' },
        c3: { title: 'Offene KI-Anbindung', text: 'Binden Sie eigene KI-Chatbots oder Assistenten an, um Projektdaten in natürlicher Sprache abzufragen und zu pflegen — über eine offene Schnittstelle (MCP-Standard).' },
        c4: { title: 'Statistiken & Reporting', text: 'Portfolio-Statistiken, Finanzen und Angebote an einem Ort.' }
      },
      usp: {
        title: 'Bewährt am größten Speicherprojekt Deutschlands.',
        subtitle: 'Produktiv im Einsatz bei über 2 GW Projektpipeline und der Steuerung des größten Speichers Deutschlands nach Projektgröße — kein Konzept, sondern gelebte Praxis.'
      },
      faq: {
        eyebrow: 'Häufige Fragen',
        title: 'Antworten auf einen Blick.',
        q1: 'Für welche Projekte ist das gedacht?',
        a1: 'Energieprojekte aller Art — PV, Batteriespeicher, Netz — von der Akquise bis in den Betrieb.',
        q2: 'Woher kommen die Dokumente für die KI?',
        a2: 'Aus allen verteilten Datenquellen des Unternehmens; der Ingest läuft automatisch.',
        q3: 'Wie funktioniert das Frühwarnsystem?',
        a3: 'Es erkennt Risiken bei Fristen, Genehmigungen und Netzanschluss proaktiv und priorisiert sie über das Portfolio.',
        q4: 'Kann ich eigene KI-Assistenten anbinden?',
        a4: 'Ja — über eine offene Schnittstelle (MCP-Standard) binden Sie eigene KI-Chatbots oder Assistenten an, um Projektdaten in natürlicher Sprache abzufragen und zu pflegen.'
      },
      cta: {
        title: 'Sehen Sie es an Ihrem Projektportfolio.',
        button: 'Demo anfragen'
      }
    },
    ai: {
      nav: {
        title: 'KI',
        overview: 'Übersicht',
        reachy: 'KI-Assistent',
        projektmanagement: 'Projektmanagement',
        useCases: 'Use Cases',
        consulting: 'Beratung',
        playground: 'KI-Playground'
      },
      reachy: {
        meta: {
          title: 'Reachy — KI-Assistent für Unternehmen | Energieglück AI',
          description: 'Reachy ist der KI-Mitarbeiter, der E-Mails beantwortet, Anrufe entgegennimmt und Probleme löst — rund um die Uhr, DSGVO-konform, Made in Germany.'
        },
        hero: {
          badge: 'NEU',
          title: 'Der KI-Mitarbeiter, der nie schläft',
          subtitle: 'Stellen Sie sich vor: Ein Mitarbeiter, der gleichzeitig E-Mails beantwortet, Anrufe entgegennimmt, Ihren Chat betreut und auf Social Media reagiert – fehlerfrei, rund um die Uhr, ohne Urlaub.',
          hook: 'Reachy ist kein Chatbot. Er denkt mit. Er erkennt, wenn etwas schiefläuft, und handelt – bevor Sie es merken.'
        },
        features: {
          title: 'Was macht Reachy besonders?',
          card1: { title: 'Alle Kommunikationskanäle', text: 'E-Mail, Chat, Telefon, Social Media – ein Assistent für alles' },
          card2: { title: 'Voller Datenzugriff', text: 'Sichere Anbindung an alle Unternehmensdaten in Echtzeit' },
          card3: { title: 'Proaktives Arbeiten', text: 'Erkennt Handlungsbedarf und agiert eigenständig' },
          card4: { title: 'Multi-Agenten-Teams', text: 'Spawnt spezialisierte KI-Kollegen für komplexe Aufgaben' },
          card5: { title: 'Enterprise-Sicherheit', text: 'Made in Germany – DSGVO-konform und auditierbar' },
          card6: { title: 'Selbstlernend', text: 'Wird mit jedem Einsatz intelligenter und effizienter' }
        },
        trust: {
          badge1: 'DSGVO-konform',
          badge2: 'Made in Germany',
          badge3: 'Enterprise-ready'
        },
        cta: {
          title: 'Sehen ist glauben. Kostenlose Live-Demo.',
          subtitle: '30 Minuten · Kein Vertriebsgespräch',
          button: 'Demo buchen'
        }
      },
      assetManagement: {
        meta: {
          title: 'KI Asset Management für PV & Batteriespeicher | Energieglück+',
          description: '96% Prognosegenauigkeit, 98% Verfügbarkeit. Energieglück+ ist die KI-Plattform für PV und BESS Asset Management mit Digital Twin und Predictive Maintenance.'
        },
        hero: {
          title: 'Der Rest monitort. Wir managen.',
          subtitle: 'Andere Systeme zeigen bunte Dashboards. Wir sagen Ihnen, wo Sie gerade Geld verlieren – und warum.'
        },
        keyNumbers: {
          title: 'Die Zahlen sprechen für sich',
          accuracy: '96% Prognosegenauigkeit',
          availability: '98% Anlagenverfügbarkeit',
          comparison: '15 min Soll-Ist-Vergleich',
          monitoring: '24/7 KI-Monitoring + Leitwarte'
        },
        digitalTwin: {
          title: 'Digital Twin – Das Herzstück',
          subtitle: 'Physikalisch-technische Sollwertberechnung statt statistischer Komponentenvergleiche. Einziges System zur objektiven Identifikation von Unterperformance – auch ohne sichtbare Ausfälle.',
          feature1: 'Modulkennlinien & meteorologische Messwerte',
          feature2: 'Verlustberechnungen (Verschattung, Leitungen)',
          feature3: 'Monetäre Verlustbewertung in Echtzeit',
          feature4: 'Predictive Maintenance vor Ausfällen'
        },
        features: {
          title: 'Alle Features auf einen Blick',
          card1: { title: 'Performance-Analyse', text: 'Identifikation von Minderleistungen auf allen Topologieebenen' },
          card2: { title: 'Automatisiertes Reporting', text: 'PAC/FAC Tests, PR-Garantie-Dokumentation' },
          card3: { title: 'BESS-Management', text: 'SoC/SoH-Überwachung, Degradationsanalyse' },
          card4: { title: 'Mensch + Maschine', text: 'KI 24/7 + eigene Leitwarte mit Asset Managern' }
        },
        cta: {
          title: 'Ihr Kraftwerk wird zum Selbstläufer.',
          button: 'Mehr erfahren'
        }
      },
      useCases: {
        meta: {
          title: 'KI Use Cases für Unternehmen — Sofort einsetzbar | Energieglück AI',
          description: '10 KI-Anwendungsfälle für Ihr Unternehmen. Von Intenterkennung bis Churn Prediction — wir integrieren KI in Ihre bestehenden Prozesse.'
        },
        hero: {
          title: 'KI-Anwendungsfälle für Stadtwerke und Energieversorger',
          subtitle: 'Pragmatisch, messbar und sofort einsetzbar — von automatisierter Kundenkommunikation bis zur intelligenten Wissensdatenbank.'
        },
        intro: {
          text: 'Fachkräftemangel, steigende Serviceerwartungen und wachsende Dokumentenflut — Stadtwerke und Energieversorger stehen vor konkreten Herausforderungen. Unsere KI-Lösungen setzen genau dort an: praxiserprobt, DSGVO-konform und in bestehende IT-Landschaften integrierbar.',
          discoverButton: 'Use Cases entdecken',
          playgroundButton: 'Im Playground ausprobieren'
        },
        clusters: {
          customerService: {
            title: 'Kundenservice & Vertrieb'
          },
          processes: {
            title: 'Prozesse & Dokumente'
          },
          knowledge: {
            title: 'Wissen & KI-Agenten'
          }
        },
        playgroundBanner: {
          title: 'Jetzt selbst ausprobieren',
          text: 'Testen Sie unsere KI-Tools in einer geschützten Demo-Umgebung — kostenlos und unverbindlich.',
          button: 'Zum KI Playground'
        },
        cases: {
          title: 'Die 10 wichtigsten Use Cases',
          case1: { 
            title: 'Intenterkennung', 
            problem: 'E-Mails landen im falschen Postfach. Kunden warten tagelang.',
            text: 'Unsere KI klassifiziert jede eingehende Nachricht in unter 2 Sekunden — und leitet sie direkt an den richtigen Ansprechpartner weiter.',
            metric: '60-90% schnellere Bearbeitung',
            before: '15 Min. manuelle Sortierung',
            after: '2 Sek. automatisch'
          },
          case2: { 
            title: 'Datenextraktion', 
            problem: 'Stunden mit Abtippen aus PDFs und Formularen verschwendet.',
            text: 'KI liest jedes Dokument, extrahiert alle wichtigen Daten und trägt sie automatisch ins richtige System ein — selbst bei handschriftlichen Notizen.',
            metric: '40% weniger Handarbeit',
            before: '20 Min/Dokument',
            after: '30 Sek/Dokument'
          },
          case3: { 
            title: 'KI-Antworten', 
            problem: 'Standardfragen kosten Zeit. Kunden warten auf einfache Antworten.',
            text: 'Die KI kennt Ihre besten Antworten und personalisiert sie für jeden Kunden — mit Ihrem Tonfall, Ihren Produkten, in perfekter Qualität.',
            metric: 'Personalisiert, qualitätsgeprüft',
            before: '45 Min Recherche',
            after: '30 Sek perfekte Antwort'
          },
          case4: { 
            title: 'Stimmungsanalyse', 
            problem: 'Verärgerte Kunden erkennt man oft zu spät.',
            text: 'Jede Nachricht wird auf Emotionen analysiert. Kritische Fälle landen sofort beim Chef — bevor der Kunde zur Konkurrenz wechselt.',
            metric: 'Automatische Priorisierung',
            before: 'Unzufriedenheit unbemerkt',
            after: 'Sofort-Alarm bei Problemen'
          },
          case5: { 
            title: 'Segmentierung', 
            problem: 'Alle Kunden bekommen die gleiche Behandlung.',
            text: 'Die KI erkennt VIP-Kunden, Neukunden und Problemfälle automatisch — und passt Kommunikation und Angebote entsprechend an.',
            metric: 'Nach Verhalten & Wert',
            before: 'One-Size-fits-All',
            after: 'Individuelle Behandlung'
          },
          case6: { 
            title: 'Churn Prediction', 
            problem: 'Kündigungen kommen überraschend.',
            text: 'Das System erkennt frühe Warnzeichen: weniger Aktivität, negative Sprache, Support-Häufung. Sie können reagieren, bevor es zu spät ist.',
            metric: 'Warnung vor Kündigung',
            before: 'Reaktive Schadensbegrenzung',
            after: 'Proaktive Kundenrettung'
          },
          case7: { 
            title: 'Personalisierung', 
            problem: 'Angebote gehen ins Leere. Timing ist schlecht.',
            text: 'Die KI erkennt den perfekten Moment: Kunde interessiert sich gerade für Zusatzprodukte? Automatisches, natürliches Cross-Selling.',
            metric: 'Natürliches Cross-Selling',
            before: 'Zufällige Angebote',
            after: 'Perfektes Timing'
          },
          case8: { 
            title: 'Bearbeitungszeit', 
            problem: 'Komplexe Anfragen dauern ewig.',
            text: 'Die KI übernimmt Recherche, Datensammlung und Vorbereitung. Ihr Team konzentriert sich auf Entscheidungen und Kundenkontakt.',
            metric: '90% Zeitersparnis',
            before: '15 Min/Anfrage',
            after: '1,5 Min/Anfrage'
          },
          case9: { 
            title: 'OCR & Dokumente', 
            problem: 'Fotos von Dokumenten manuell abtippen.',
            text: 'Einfach Handy-Foto machen. Die KI erkennt alle Texte, Zahlen und Daten — egal ob schief fotografiert, handgeschrieben oder verknittert.',
            metric: 'Egal wie schief',
            before: 'Manuell abtippen',
            after: '3 Sek im System'
          },
          case10: { 
            title: 'Wissensdatenbank', 
            problem: 'Firmen-Know-how ist in Köpfen gefangen.',
            text: 'Die KI lernt aus allen E-Mails, Dokumenten und Gesprächen. Jeder Mitarbeiter hat sofort Zugang zum gesamten Unternehmenswissen.',
            metric: 'Lernt mit, vergisst nie',
            before: 'Wissen in Silos',
            after: 'Kollektive Intelligenz'
          }
        },
        socialProof: {
          title: '',
          stat1: '10 Use Cases sofort einsetzbar',
          stat2: '0 Tage Setup für Standardlösungen',
          stat3: '100% DSGVO — Hosting in Deutschland',
          stat4: '∞ skalierbar, lernt mit',
          quote: '"KI ist unsere DNA — wir reden nicht darüber, wir leben es. Jeden Tag, in jedem Prozess."',
          author: 'Dr. Nico Haberer, Gründer Energieglück Gruppe'
        },
        comparison: {
          title: 'Vorher vs. Nachher: Der KI-Unterschied',
          subtitle: 'Konkrete Zahlen aus echten Projekten',
          tableHeaders: {
            process: 'Prozess',
            manual: 'Manuell',
            withAI: 'Mit KI'
          },
          processes: [
            {
              name: 'E-Mail-Sortierung',
              manual: '15 Min/Mail',
              ai: '2 Sek/Mail'
            },
            {
              name: 'Dokumentenextraktion',
              manual: '20 Min/Dokument',
              ai: '30 Sek/Dokument'
            },
            {
              name: 'Netzauswirkungsanalyse',
              manual: '200 Stunden',
              ai: '20 Stunden'
            },
            {
              name: 'Fehlerquote Datenerfassung',
              manual: '8-12%',
              ai: 'unter 0,5%'
            },
            {
              name: 'Support-Verfügbarkeit',
              manual: 'Mo-Fr 9-17',
              ai: '24/7'
            }
          ]
        },
        faq: {
          title: 'Häufig gestellte Fragen',
          questions: [
            {
              question: 'Welcher Use Case passt zu meinem Unternehmen?',
              answer: 'Das hängt von Ihren größten Zeitfressern ab. E-Mail-lastige Unternehmen profitieren von Intenterkennung und KI-Antworten. Dokumenten-lastige Branchen lieben OCR und Datenextraktion. In einem 30-minütigen Gespräch finden wir die 3 wichtigsten Use Cases für Sie.'
            },
            {
              question: 'Wie lange dauert die Integration?',
              answer: 'Die ersten Use Cases sind in 2-4 Wochen produktiv. Komplexere Integrationen dauern 6-12 Wochen. Wir starten immer mit dem größten Quick Win und bauen dann schrittweise aus.'
            },
            {
              question: 'Ist die KI DSGVO-konform?',
              answer: '100% ja. Alle Daten bleiben in Deutschland, werden verschlüsselt verarbeitet und können jederzeit gelöscht werden. Wir arbeiten nur mit DSGVO-zertifizierten Partnern und führen regelmäßige Audits durch.'
            },
            {
              question: 'Wie funktioniert die Integration in unsere Prozesse?',
              answer: 'Wir integrieren unsere KI-Lösungen individuell in Ihre bestehenden oder neuen Prozesse — kein Standardprodukt von der Stange. Ob E-Mail, CRM, ERP oder Telefonie: Wir passen uns an Ihre Infrastruktur an, nicht umgekehrt.'
            },
            {
              question: 'Was kostet es?',
              answer: 'Die Kosten hängen von der Anzahl der Use Cases und Benutzer ab. Ein typisches Setup für 10-50 Mitarbeiter startet bei 800€/Monat. Das zahlt sich meist schon durch die Zeitersparnis der ersten Woche aus. Genaue Preise nach dem kostenlosen Beratungsgespräch.'
            }
          ]
        },
        cta: {
          title: 'Kostenlose Erstberatung buchen',
          subtitle: 'In 30 Minuten finden wir die wichtigsten KI-Hebel für Ihr Stadtwerk — unverbindlich und praxisnah.',
          button: 'Jetzt Termin vereinbaren'
        }
      }
    },
    ki: {
      meta: {
        title: 'KI für die Energiewende — Energieglück AI GmbH | Energieglück',
        description: 'Die Energieglück AI GmbH entwickelt intelligente Lösungen für die Energiebranche und berät Unternehmen bei der digitalen Transformation.'
      },
      hero: {
        title: 'KI für die Energiewende — und für Ihr Unternehmen',
        subtitle: 'Die Energieglück AI GmbH entwickelt intelligente Lösungen für die Energiebranche und berät Unternehmen bei der digitalen Transformation.',
        cta: 'Kostenlose Erstberatung buchen'
      },
      products: {
        title: 'Unsere Lösungen',
        subtitle: 'Von Asset Management über KI-Assistenten bis hin zur strategischen Beratung — wir machen KI nutzbar.',
        egplus: {
          title: 'Energieglück+',
          desc: 'KI Asset Management Software mit digitalem Zwilling, Predictive Maintenance und Echtzeit-Monitoring für maximale Anlagenperformance.'
        },
        reachy: {
          title: 'Reachy',
          desc: 'KI-Assistent für E-Mail, Telefon und Chat — automatisiert Kundenkommunikation, 24/7, DSGVO-konform, Made in Germany.'
        },
        consulting: {
          title: 'KI-Beratung',
          desc: 'Von der Bestandsaufnahme über Quick Wins bis zur langfristigen KI-Strategie. Wir begleiten Ihr Unternehmen Schritt für Schritt.'
        },
        learnMore: 'Mehr erfahren'
      },
      steps: {
        title: 'Unser Beratungsansatz',
        subtitle: 'In vier Schritten zur erfolgreichen KI-Integration.',
        step1: {
          title: 'Bestandsaufnahme',
          desc: 'Wo stehen Sie? Prozesse, Tools, Datenquellen.'
        },
        step2: {
          title: 'Konzept',
          desc: 'Datenschutz, IT-Landschaft, Quick Wins identifizieren.'
        },
        step3: {
          title: 'Implementierung',
          desc: 'Schrittweise Integration, Schulung, Change Management.'
        },
        step4: {
          title: 'KI-Strategie',
          desc: 'Langfristige Roadmap, Monitoring, Weiterentwicklung.'
        },
        cta: 'Kostenlose Erstberatung buchen'
      },
      reference: {
        label: 'Referenz',
        text: 'Stadtwerke Göttingen AG setzt auf unsere KI-Lösungen.'
      }
    },
    playground: {
      registration: {
        title: 'Zugang zum KI-Playground anfordern',
        subtitle: 'Testen Sie unsere KI-Services mit echten Szenarien. Wir senden Ihnen einen persönlichen Zugangslink per E-Mail.',
        company: 'Firma',
        companyPlaceholder: 'Stadtwerke Musterstadt GmbH',
        name: 'Name',
        namePlaceholder: 'Max Mustermann',
        email: 'E-Mail',
        emailPlaceholder: 'max.mustermann@stadtwerke.de',
        phone: 'Telefon (optional)',
        phoneRequired: 'Telefon',
        phonePlaceholder: '+49 123 456789',
        phoneError: 'Bitte geben Sie Ihre Telefonnummer an.',
        topicsLabel: 'Welche KI-Themen interessieren Sie?',
        topic: {
          textAnalysis: 'Textanalyse',
          documentAI: 'Dokumenten-KI',
          chatbots: 'Chatbots',
          agenticAI: 'Agentic AI'
        },
        privacyText: 'Ich stimme zu, dass meine Daten zur Bereitstellung des KI Playgrounds und für vertriebliche Kontaktaufnahme verarbeitet werden. Details in der Datenschutzerklärung.',
        privacyRequired: 'Bitte stimmen Sie der Datenverarbeitung zu.',
        fieldRequired: 'Dieses Feld ist erforderlich.',
        invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
        submit: 'Zugang anfordern',
        submitting: 'Wird gesendet...',
        submitError: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
        successTitle: 'Anfrage erhalten',
        successText: 'Wir haben Ihnen einen persönlichen Zugangslink per E-Mail gesendet. Bitte prüfen Sie auch Ihren Spam-Ordner.'
      }
    },
    footer: {
      company: 'Unternehmen',
      about: 'Über uns',
      projects: 'Projekte',
      press: 'News & Presse',
      career: 'Karriere',
      solutions: 'Lösungen',
      noiseAssessment: 'Schallprognose',
      targets: 'Zielgruppen',
      landowners: 'Flächeneigentümer',
      operators: 'Netzbetreiber',
      municipalities: 'Kommunen',
      service: 'Service',
      contact: 'Kontakt',
      imprint: 'Impressum',
      privacy: 'Datenschutz',
      platform: 'Energieglück+',
      whitepaper: 'Whitepaper',
      copyright: '© 2026 Energieglück Gruppe'
    },
    landowners: {
      hero: {
        eyebrow: 'Für Flächeneigentümer',
        title: 'Aus Ihrer Fläche wird stabile, langfristige Pacht.',
        lede: 'Wir entwickeln, bauen und betreiben Großbatteriespeicher in Deutschland. Sie verpachten — wir tragen Investition, Genehmigung und Betrieb.',
        ctaPrimary: 'Fläche kostenfrei prüfen',
        ctaSecondary: 'So läuft es ab'
      },
      kpi: {
        years: { value: '20+', label: 'Jahre Pachtdauer' },
        hectare: { value: 'ab 1.000 m²', label: 'Mindestfläche' },
        invest: { value: '0 €', label: 'Ihre Investition' },
        payouts: { value: 'monatlich', label: 'Planbare Zahlungen' }
      },
      intro: {
        title: 'Langfristige Partnerschaft',
        description: 'Energieglück sichert Ihnen attraktive Pacht- oder Kaufpreise – wir setzen auf Partnerschaften, die über Jahrzehnte halten. Nachhaltig genutzte Flächen schaffen echten Mehrwert. Haben Sie ein Grundstück und Fragen zum Ablauf oder zur Eignung? Kontaktieren Sie uns!'
      },
      contact: {
        title: 'Direktkontakt für Eigentümer',
        formButton: 'Kontaktformular'
      },
      value: {
        eyebrow: 'Was es für Sie bedeutet',
        title: 'Ein Vertrag. Drei klare Vorteile.',
        card1: {
          title: 'Planbare Einnahmen',
          desc: 'Feste, indexierte Pachtzahlungen über die gesamte Vertragslaufzeit — unabhängig von Strompreis und Marktlage.'
        },
        card2: {
          title: 'Kein Risiko, keine Kosten',
          desc: 'Wir übernehmen Planung, Genehmigung, Bau, Versicherung und Rückbau. Sie behalten das Eigentum an Ihrer Fläche.'
        },
        card3: {
          title: 'Fläche bleibt nutzbar',
          desc: 'Speicher belegen nur einen kleinen Teil. Beweidung und Randnutzung bleiben oft möglich — kein Bodenversiegelung wie beim Hochbau.'
        }
      },
      benefits: {
        battery: {
          title: 'Warum Batteriespeicher?',
          description: 'Unsere innovativen Batteriespeicher sichern die Netzstabilität und maximieren den Ausbau erneuerbarer Energien. Mit Ihrer Fläche tragen Sie aktiv dazu bei, Stromkosten zu reduzieren und eine nachhaltige Energieversorgung für kommende Generationen zu gewährleisten.'
        },
        energieglueck: {
          title: 'Warum Energieglück?',
          description: 'Wir entwickeln, bauen und betreiben Batteriespeicher für die Energieglück Gruppe – von der Planung bis zum langfristigen Betrieb. Mit jahrelanger Erfahrung realisieren wir nachhaltige Projekte auf Ihrer Fläche.'
        }
      },
      process: {
        eyebrow: 'So läuft es ab',
        title: 'Vier Schritte. Klar und ohne Vorleistung Ihrerseits.',
        step1: {
          title: 'Kontaktaufnahme',
          description: 'Sie schreiben uns über das Kontaktformular oder per E-Mail mit ein paar Eckdaten zur Fläche.',
          duration: 'Tag 1'
        },
        step2: {
          title: 'Flächenprüfung',
          description: 'Netzanschluss, Topografie, Genehmigungsfähigkeit — meist haben wir innerhalb eines Tages eine erste belastbare Einschätzung für Sie.',
          duration: 'meist 24 Stunden'
        },
        step3: {
          title: 'Individuelles Angebot',
          description: 'Sie erhalten ein konkretes Angebot zu Pacht oder Kauf, inklusive Konditionen und Laufzeit.',
          duration: 'zeitnah'
        },
        step4: {
          title: 'Vertragsabschluss',
          description: 'Notarielle Beurkundung, Eintragung der beschränkten persönlichen Dienstbarkeit, danach übernehmen wir alles.',
          duration: 'auf Ihren Wunsch'
        }
      },
      cta: {
        title: 'Ihre Fläche könnte geeignet sein.',
        description: 'Senden Sie uns Lage und Größe — wir prüfen kostenfrei und melden uns schnell zurück.',
        button: 'Fläche prüfen lassen'
      },
      faq: {
        title: 'Häufig gestellte Fragen',
        question1: {
          question: 'Welche Flächen sind geeignet?',
          answer: 'Grundsätzlich sind Flächen ab ca. 1 Hektar geeignet. Wichtig sind ein Netzanschluss in der Nähe und eine ebene Beschaffenheit. Wir prüfen gerne die Eignung Ihrer Fläche.'
        },
        question2: {
          question: 'Wie lange läuft der Pachtvertrag?',
          answer: 'Unsere Pachtverträge laufen in der Regel über 20+ Jahre und bieten Ihnen langfristige, planbare Einnahmen.'
        },
        question3: {
          question: 'Was passiert nach Vertragsende?',
          answer: 'Nach Vertragsende können wir die Anlage entweder zurückbauen oder den Vertrag verlängern – je nach Ihrer Präferenz.'
        }
      }
    },
    gridoperators: {
      title: 'Netzbetreiber',
      intro: {
        title: 'GridStable für ein stabiles Stromnetz',
        text: 'GridStable ist unser Konzept für netzdienliche Batteriespeicher. Durch intelligente Steuerung und schnelle Reaktionszeiten stabilisieren unsere BESS-Systeme das Stromnetz effektiv.'
      },
      gridstable: {
        title: 'GridStable',
        text: 'GridStable ist unser Konzept für netzdienliche Batteriespeicher. Durch intelligente Steuerung und schnelle Reaktionszeiten stabilisieren unsere BESS-Systeme das Stromnetz effektiv.',
        feature1: 'Frequenzstabilität durch BESS',
        feature2: 'Spannungshaltung im Netz',
        feature3: 'Systemdienstleistungen (SDL)',
        feature4: 'Blackstart-Fähigkeit für Netzwiederaufbau'
      },
      benefits: {
        title: 'Vorteile für Netzbetreiber',
        stability: { title: 'Netzstabilität', text: 'Ausgleich von Schwankungen und Sicherstellung einer stabilen Stromversorgung rund um die Uhr.' },
        efficiency: { title: 'Effizienzsteigerung', text: 'Optimierte Netzauslastung und Reduzierung von Engpässen durch intelligentes Lastmanagement.' },
        flexibility: { title: 'Flexibilität', text: 'Schnelle Reaktionszeiten und flexible Bereitstellung von Regelenergie nach Bedarf.' }
      },
      integration: {
        title: 'Technische Integration',
        connection: { title: 'Netzanschluss', text: 'Nahtlose Integration in bestehende Netzinfrastrukturen mit standardisierten Schnittstellen und Protokollen. Unsere Systeme sind kompatibel mit allen gängigen Netzbetreiber-Standards.' },
        control: { title: 'Steuerung & Monitoring', text: '24/7 Echtzeitüberwachung und -steuerung über unser Energieglück+ System. Vollständige Transparenz über alle relevanten Betriebsparameter und automatisierte Optimierung.' },
        compliance: { title: 'Standards & Compliance', text: 'Erfüllung aller relevanten Normen und Richtlinien (VDE-AR-N, TAR, EEG). Zertifizierte Systeme und regelmäßige Audits garantieren höchste Sicherheit.' }
      },
      asset: {
        title: 'Asset Management mit Energieglück+',
        text: 'Netzbetreiber verfügen häufig über eigene PV-Parks und Energieanlagen. Unser Energieglück+ System bietet:',
        feature1: 'Digitaler Zwilling für PV-Parks und Batteriespeicher',
        feature2: 'Day-Ahead Forecasting für optimale Planung',
        feature3: 'Predictive Maintenance zur Ausfallvermeidung',
        feature4: 'Echtzeit-Performance-Optimierung',
        feature5: 'Automatisierte Anomalieerkennung',
        button: 'Mehr über Energieglück+'
      },
      projects: {
        title: 'Regionale Projekte & Referenzen',
        description: 'Netzdienlicher Batteriespeicher zur Unterstützung der regionalen Netzstabilität.',
        button: 'Alle Projekte ansehen',
        construction: 'Im Bau',
        operational: 'In Betrieb',
        inverter: 'Wechselrichter',
        storage: 'Speicher'
      },
      whyUs: {
        eyebrow: 'Warum Energieglück',
        title: 'Mehr als ein Projektentwickler.',
        text: 'Wir sind mehr als nur ein Projektentwickler – wir sind Ihr langfristiger Partner für netzdienliche Batteriespeicher',
        holistic: { title: 'Ganzheitlicher Ansatz', text: 'Entwicklung, Bau und Betrieb aus einer Hand – von der ersten Planung bis zum langfristigen Anlagenbetrieb.' },
        pipeline: { title: '2GW+ Pipeline', text: 'Starke Projektpipeline mit über 2 GW in Entwicklung für kontinuierliche Netzstabilisierung.' },
        partnership: { title: 'Langfristige Partnerschaft', text: 'Wir betreiben unsere Speicher selbst und sind über Jahrzehnte Ihr verlässlicher Ansprechpartner.' },
        technology: { title: 'Innovative Technologie', text: 'GridStable für netzdienliche Steuerung und Energieglück+ für intelligentes Asset Management.' }
      },
      hero: {
        eyebrow: 'Für Übertragungs- und Verteilnetzbetreiber',
        title: 'Netzdienliche Speicher, die wirklich entlasten.',
        lede: 'Wir entwickeln, bauen und betreiben Batteriespeicher, die Frequenz und Spannung stabilisieren — und im Ernstfall schwarzstartfähig sind.',
        ctaPrimary: 'Projektgespräch anfragen',
        ctaSecondary: 'Wie wir arbeiten'
      },
      kpi: {
        pipeline: { value: '2 GW+', label: 'Projektpipeline' },
        realtime: { value: '24/7', label: 'Echtzeit-Steuerung' },
        compliance: { value: 'VDE-AR-N', label: 'Konform & zertifiziert' },
        blackstart: { value: '100%', label: 'Schwarzstartfähig' }
      },
      value: {
        eyebrow: 'Was wir liefern',
        title: 'Drei Bausteine für ein stabileres Netz.',
        card1: {
          title: 'Netzdienliche Steuerung',
          desc: 'Unsere BESS-Systeme reagieren in Millisekunden und stützen Frequenz wie Spannung — auch in kritischen Lastfenstern.',
          bullet1: 'Frequenzstabilität (FCR, aFRR, mFRR)',
          bullet2: 'Spannungshaltung & Blindleistung',
          bullet3: 'Schwarzstart für Netzwiederaufbau'
        },
        card2: {
          title: 'Nahtlose Integration',
          desc: 'Standardisierte Schnittstellen und Protokolle — kompatibel mit allen gängigen Netzbetreiber-Standards.',
          bullet1: 'VDE-AR-N, TAR und EEG konform',
          bullet2: 'Direktanbindung an Leitwarten',
          bullet3: 'Zertifizierte Anlagen, regelmäßige Audits'
        },
        card3: {
          title: 'Asset Management mit Energieglück+',
          desc: 'Digitaler Zwilling, Day-Ahead-Forecasting und Predictive Maintenance — auch für Ihre eigenen PV-Parks.',
          bullet1: 'Digitaler Zwilling für PV & BESS',
          bullet2: 'Day-Ahead Forecasting',
          bullet3: 'Predictive Maintenance & Anomalieerkennung'
        }
      },
      approach: {
        eyebrow: 'Wie wir arbeiten',
        title: 'GridStable: Marktdienst und Netzdienst zusammendenken.',
        description: 'Statt Batterien rein gewinnorientiert zu fahren, identifizieren unsere Algorithmen kritische Engpassfenster und positionieren die Speicher genau dann entlastend — bei minimalem Erlösverlust.',
        bullet1: 'Historische Mustererkennung kritischer Engpassstunden',
        bullet2: 'Proaktive SOC-Positionierung vor absehbaren Lastfenstern',
        bullet3: 'Dual-Purpose-Optimierung: Marktdienst + Netzdienst',
        bullet4: 'Transparente Reporting-Ebene für Netzbetreiber',
        cta: 'GridStable im Detail'
      },
      cta: {
        title: 'Konkreter Engpass im Netzgebiet?',
        lede: 'Sprechen wir über Ihre Lastflüsse, Ihre Schwachstellen und wie ein Speicher genau dort entlasten kann.',
        button: 'Projektgespräch anfragen',
        text: 'Kontaktieren Sie uns für weitere Informationen über unsere Batteriespeicher-Projekte'
      }
    },
    municipalities: {
      title: 'Kommunen',
      hero: {
        eyebrow: 'Für Bürgermeister und kommunale Entscheider',
        title: 'Energiestandort der Zukunft. Ohne Eigeninvestition.',
        lede: '90 % der Gewerbesteuer bleiben in Ihrer Kommune — bei voller Privatfinanzierung und planbaren Einnahmen über 15+ Jahre.',
        credibility: 'Seit 2023 BESS-Pionier mit eigenem Standort-Speicher (Partnerprojekt, Inbetriebnahme 2026) — und Entwickler des größten Speichers Deutschlands nach Projektgröße.',
        ctaPrimary: 'Beratungsgespräch vereinbaren',
        ctaSecondary: 'Wie wir arbeiten'
      },
      kpi: {
        tax: { value: '90%', label: 'Gewerbesteuer lokal' },
        invest: { value: '0 €', label: 'Eigeninvestition' },
        duration: { value: '15+ J.', label: 'Planbare Laufzeit' },
        private: { value: '100%', label: 'Privatfinanziert' }
      },
      intro: {
        title: 'Ihr Weg zum zukunftsfähigen Energiestandort',
        text: 'Hohe Gewerbesteuereinnahmen, Vorreiterrolle in der Energiewende und Grundlage für E-Mobilität, Wärmenetze und grünen Wasserstoff',
        cta1: 'Jetzt beraten lassen',
        cta2: 'Referenzprojekte ansehen'
      },
      benefits: {
        eyebrow: 'Was Sie davon haben',
        title: 'Drei messbare Vorteile für Ihre Kommune.',
        subtitle: 'Finanziell, strategisch und zukunftsorientiert — ohne kommunales Risiko.',
        tax: {
          title: 'Hohe langfristige Gewerbesteuereinnahmen',
          highlight: '90% verbleiben direkt in Ihrer Kommune',
          text: 'seit Jahressteuergesetz 2024 – ohne Risiko, ohne Eigeninvestition.',
          feature1: 'Planbare Einnahmen über die gesamte Betriebsdauer (15+ Jahre)',
          feature2: '100% private Investition – keine kommunalen Kosten',
          feature3: 'Lokale Wertschöpfung durch regionale Aufträge'
        },
        pioneer: {
          title: 'Vorreiterrolle in der Energiewende',
          highlight: 'Leuchtturmprojekt',
          text: 'und erreichen Sie Klimaneutralität bis 2030/2045 — umgesetzt mit Energieglück, dem BESS-Pionier seit 2023.',
          feature1: 'Verifizierte CO₂-Einsparungen',
          feature2: 'Internationales Renommee als Energiepionier',
          feature3: 'Attraktiv für zukunftsfähige Unternehmensansiedlung'
        },
        location: {
          title: 'Zukunftsfähiger Energiestandort',
          highlight: 'Infrastruktur-Grundlage',
          text: 'für E-Mobilität, Wärmenetze und grünen Wasserstoff.',
          feature1: 'Ladeinfrastruktur mit lokalem grünem Strom',
          feature2: 'Dekarbonisierte Wärmeversorgung möglich',
          feature3: 'Wasserstoff-Produktion für Industrie & Mobilität'
        }
      },
      energyHub: {
        eyebrow: 'Das Schlüssel-Konzept',
        title: 'Mehr als eine Anlage — ein Energiestandort.',
        subtitle: 'Der Speicher ist die Infrastruktur-Grundlage für E-Mobilität, Wärmenetze und grünen Wasserstoff.',
        emobility: {
          title: 'E-Mobilität & Ladeinfrastruktur',
          text: 'Ladeparks nutzen lokal gespeicherten grünen Strom. Schnellladehubs werden erst mit Speichern wirtschaftlich.',
          feature1: 'Netzdienliche Ladeoptimierung',
          feature2: 'Kein Netzausbau erforderlich',
          feature3: 'Reduktion von Netzentgelten'
        },
        heating: {
          title: 'Wärmeversorgung & Sektorkopplung',
          text: 'Großwärmepumpen nutzen Speicherstrom zur dekarbonisierten Wärmeversorgung für Quartiere.',
          feature1: 'CO₂-freie Wärmeversorgung',
          feature2: 'Nahwärmenetze für Stadtteile',
          feature3: 'Alternative zu Gas und Öl'
        },
        hydrogen: {
          title: 'Wasserstoff-Infrastruktur',
          text: 'Grüner Wasserstoff durch Elektrolyse mit Speicherstrom für Industrie und Mobilität.',
          feature1: 'Industrielle Anwendungen',
          feature2: 'H₂-Tankstellen für Busse & LKW',
          feature3: 'Langzeitspeicherung möglich'
        },
        strategic: {
          title: 'Strategische Vorteile des Energiestandorts',
          efficiency: { title: 'Effiziente Flächennutzung', text: 'Mehrere Energiefunktionen an einem Standort' },
          approval: { title: 'Reduzierter Genehmigungsaufwand', text: 'Zentrale Infrastruktur statt Einzelprojekte' },
          value: { title: 'Maximierte Wertschöpfung', text: 'Pro Quadratmeter und pro Euro Investition' },
          future: { title: 'Zukunftssicherheit', text: 'Flexible Erweiterbarkeit ohne Neuinvestitionen' }
        }
      },
      whyUs: {
        eyebrow: 'Warum Energieglück',
        title: 'Ein Partner, der Ihre Kommune versteht.',
        subtitle: 'Wir bringen Expertise, Innovation und langjährige Erfahrung für Ihr kommunales Energieprojekt',
        holistic: { title: 'Ganzheitlicher Ansatz', text: 'Nicht nur Speicher – wir denken das gesamte Energiestandort-Konzept mit E-Mobilität, Wärme und Wasserstoff mit' },
        pipeline: { title: '2 GW+ Pipeline', text: 'Umfangreiche Projekterfahrung und bewährte Prozesse für zuverlässige Umsetzung' },
        partnership: { title: 'Langfristige Partnerschaft', text: 'Wir begleiten Sie über die gesamte Projektlaufzeit – von Planung bis Betrieb über 15+ Jahre' },
        technology: { title: 'Innovative Technologie', text: 'KI-gestützte Entwicklung mit Energieglück+ für optimale Standortwahl und Wirtschaftlichkeit' }
      },
      approach: {
        eyebrow: 'Wie wir arbeiten',
        title: 'Partnerschaft auf Augenhöhe.',
        description: 'Wir verstehen uns als langfristiger Partner Ihrer Kommune. Transparenz, Dialog und vertrauensvolle Zusammenarbeit sind die Grundlage unserer Projekte.',
        bullet1: 'Offene Kommunikation mit Gemeinderat und Verwaltung',
        bullet2: 'Transparente Projektvorstellung für alle Beteiligten',
        bullet3: 'Einbindung lokaler Akteure von Anfang an',
        bullet4: 'Langfristige Begleitung über die gesamte Projektlaufzeit (15+ Jahre)'
      },
      projects: {
        title: 'Unsere Referenzprojekte',
        subtitle: 'Wir realisieren deutschlandweit Batteriespeicher-Projekte für eine nachhaltige Energiezukunft'
      },
      faq: {
        eyebrow: 'Häufige Fragen',
        title: 'Was Kommunen uns oft fragen.',
        q1: 'Wir haben keinen passenden Standort — was nun?',
        a1: 'Wir prüfen das gemeinsam: KI-gestützte Standortbewertung und langjährige Scouting-Expertise gehören zu unserem Prozess.',
        q2: 'Trägt die Kommune ein Risiko oder Kosten?',
        a2: '100 % privatfinanziert, 0 € Eigeninvestition — kein kommunales Risiko, planbare Einnahmen über 15+ Jahre.',
        q3: 'Wie sicher ist die Umsetzung?',
        a3: 'Energieglück ist BESS-Pionier seit 2023 mit über 2 GW Pipeline und bewährten Prozessen von Scouting bis Betrieb.',
        q4: 'Wie läuft die Zusammenarbeit mit Gemeinderat und Verwaltung?',
        a4: 'Transparenter Dialog und lokale Einbindung von Anfang an — wir begleiten über die gesamte Projektlaufzeit.'
      },
      cta: {
        title: 'Bereit für die Energiewende?',
        text: 'Lassen Sie uns gemeinsam Ihre Kommune zum zukunftsfähigen Energiestandort machen',
        button: 'Jetzt Beratungsgespräch vereinbaren'
      }
    },
    gridstable: {
      hero: {
        badge: 'Intelligente Netzsteuerung',
        subtitle: 'Netzdienliche Batteriespeicher durch intelligente Steuerung',
        description: 'GridStable transformiert Batteriespeicher von reinen Gewinnmaximierern zu aktiven Netzhelfern. Durch intelligente Algorithmen analysieren wir historische Netzdaten, identifizieren kritische Engpässe und steuern Batterien so, dass sie genau in den Momenten Entlastung bieten, wenn das Netz sie braucht – während gleichzeitig Markterlöse optimiert werden.'
      },
      features: {
        title: 'Was macht GridStable?',
        analysis: {
          title: 'Netzanalyse',
          subtitle: 'Das Nervensystem verstehen',
          point1: '<strong>Historische Mustererkennung:</strong> Wir verarbeiten Jahre von Viertelstunden-Messdaten und erkennen, wann und wo Engpässe entstehen',
          point2: '<strong>Zeitliche Muster:</strong> Montags um 18:00 Uhr? Freitags bei Sonnenschein? Winterliche Spitzenlast? Wir finden die Regelmäßigkeiten',
          point3: '<strong>Kritische Fenster:</strong> Das System identifiziert die 200-300 wichtigsten Stunden im Jahr, in denen Netzentlastung den größten Wert schafft',
          point4: '<strong>Ursachen-Analyse:</strong> Welche Faktoren treiben Engpässe? Wetter, Industrie-Großverbraucher, PV-Einspeisung?'
        },
        algorithms: {
          title: 'Intelligente Fahrplan-Algorithmen',
          subtitle: 'Dual-Purpose Optimierung',
          forGrid: {
            title: 'Für das Netz:',
            point1: 'Proaktive Positionierung für absehbare Engpässe',
            point2: 'Netzfreundliche Einschränkungen in kritischen Momenten',
            point3: 'Optimiertes SOC-Management'
          },
          forMarket: {
            title: 'Für den Markt:',
            point1: 'Teilnahme an Regelenergie-Märkten (FCR, aFRR, mFRR)',
            point2: 'Erfassung kurzfristiger Intraday-Trading-Chancen',
            point3: 'Machine Learning Preis-Prognosen (24-48h)'
          }
        },
        integration: {
          title: 'Seamless Integration',
          subtitle: 'Industriestandards',
          description: 'GridStable spricht die Sprache Ihrer bestehenden Infrastruktur:',
          point1: '<strong>IEC 61850 & IEC 60870-5-104:</strong> Standard-Protokolle für Netzbetreiber-Kommunikation',
          point2: '<strong>Modbus:</strong> Für lokale Geräte (Batteriesystem, Wechselrichter, Sensoren)',
          point3: '<strong>SCADA-Gateways:</strong> Hardware-Router verbinden Cloud-Optimierung mit OT-Infrastruktur',
          point4: '<strong>VDE-AR-N 4110 compliant:</strong> Spannungshaltung, Frequenzregelung, Blindleistungsbereitstellung'
        },
        monitoring: {
          title: 'Transparentes Monitoring',
          subtitle: 'Vertrauen durch Daten',
          description: 'Ein Echtzeit-Dashboard zeigt Netzbetreibern kontinuierlich:',
          point1: 'Wie viel Netzentlastung wir gerade liefern',
          point2: 'Welche Einschränkungen wir aktuell einfahren',
          point3: 'Historische Performance gegen vereinbarte KPIs',
          point4: 'Automatisierte Compliance-Berichte'
        }
      },
      benefits: {
        title: 'Der Mehrwert für Netzbetreiber',
        subtitle: 'Konkrete Vorteile',
        point1: '10-20% Reduktion von Redispatch-Kosten in kritischen Engpass-Regionen',
        point2: 'Verzögerung oder Vermeidung teurer Netzausbau-Investitionen durch intelligente Lastverteilung',
        point3: 'Sicherer Ausbau erneuerbarer Energien ohne Stabilitätsrisiken',
        point4: 'Bessere Prognose und Vorhersehbarkeit von Speicherverhalten in Ihrem Netz',
        point5: 'Transparente Echtzeit-Überwachung aller netzdienlichen Maßnahmen'
      },
      comparison: {
        title: 'Warum GridStable anders ist',
        aspect: 'Aspekt',
        conventional: 'Konventionelle Speicher',
        gridstable: 'GridStable',
        row1: {
          aspect: 'Fokus',
          conventional: 'Gewinnmaximierung',
          gridstable: '✓ Gewinn + Netzstabilität'
        },
        row2: {
          aspect: 'Planung',
          conventional: 'Tagesweise',
          gridstable: '✓ 24-48h vorausschauend'
        },
        row3: {
          aspect: 'Netzwissen',
          conventional: 'Rudimentär',
          gridstable: '✓ Datengestützte Engpass-Analyse'
        },
        row4: {
          aspect: 'Integration',
          conventional: 'Manuelle SCADA-Anbindung',
          gridstable: '✓ Automatisiert, industriestandard'
        },
        row5: {
          aspect: 'Transparenz',
          conventional: 'Black Box',
          gridstable: '✓ Echtzeit-Dashboard für Netzbetreiber'
        }
      },
      cta: {
        title: 'Interesse an GridStable?',
        description: 'Lassen Sie uns besprechen, wie GridStable Ihr Netz stabilisieren kann',
        button: 'Jetzt Kontakt aufnehmen'
      }
    },
    gridSimulator: {
      title: 'GridStable Netzlast-Simulator',
      subtitle: 'Wie Batteriespeicher das Stromnetz stabilisieren',
      chart: {
        yAxis: 'Netzauslastung (%)',
        criticalLimit: 'Kritische Grenze (75%)',
        without: 'Ohne GridStable',
        with: 'Mit GridStable'
      },
      tooltip: {
        time: 'Uhr',
        without: 'Ohne GridStable',
        with: 'Mit GridStable'
      },
      slider: {
        label: 'Speichergröße'
      },
      metrics: {
        peakReduction: {
          label: 'Spitzenlast-Reduktion'
        },
        criticalHours: {
          label: 'Kritische Stunden',
          description: 'Pro Tag eingespart'
        },
        redispatchSavings: {
          label: 'Redispatch-Einsparung',
          description: 'Pro Jahr'
        },
        marketRevenue: {
          label: 'Markterlöse',
          description: 'Durch Optimierung'
        }
      },
      info: '<strong>Szenario:</strong> Durchschnittlicher Sommertag mit PV-Einspeisung in einem regionalen Netz mit 100 MW Spitzenlast. <strong>Methodik:</strong> Eigene wissenschaftliche Simulationen und Berechnungen basierend auf BDEW-Lastprofilen, SMARD-Netzdaten und aktuellen Laststudien.'
    },
    about: {
      hero: {
        headline: 'Energie. Neu gedacht. Für Generationen gemacht.',
        subtitle: 'Wir verbinden 17 Jahre Energiewirtschaft mit modernster KI und der Stabilität einer Familienstiftung.'
      },
      intro: {
        title: 'Über Energieglück',
        text: 'Energieglück ist in zwei Bereichen aktiv: BESS und KI. Wir entwickeln und betreiben Batteriespeicher mit einer Pipeline von mehr als 2 GW; in Völklingen geht 2026 unser erstes Projekt in Betrieb. Mit Energieglück+, KI-Agenten und gezielter KI-Beratung unterstützen wir Unternehmen zudem bei Asset Management und Digitalisierung.'
      },
      story: {
        text: '2009 landete Dr. Nico Haberer eher zufällig in der Energiewirtschaft — aber mit dem festen Vorsatz, etwas zu verändern. Als SAP-Berater startend, baute er über mehr als ein Jahrzehnt gemeinsam mit Partnern eine Unternehmensberatung mit 70 Mitarbeitern auf. Doch als er begann, mit Massendaten eigene Modelle zu trainieren, und sah: das funktioniert — wurde ihm klar, dass KI die Zukunft ist. Der ChatGPT-Moment bestärkte ihn darin. Seine Partner folgten dieser Vision nicht. Also löste er sich und gründete Energieglück.',
        quote: 'Neue Innovation als Erste ausprobieren — und wenn sie Sinn macht, überall integrieren. Beruflich und privat.',
        closing: 'Die Idee für Batteriespeicher entstand durch ein Grundstück der Familienstiftung — und der Erkenntnis, dass echte Werte für die nächste Generation nicht in Beratungsverträgen stecken, sondern in Infrastruktur und Technologie.'
      },
      team: {
        title: 'Die Menschen hinter Energieglück',
        p0: { name: 'Kathy Ensch', role: 'Geschäftsführerin Energieglück Batteriespeicher GmbH & Energieglück AI GmbH', desc: 'Das Bindeglied zwischen Technik und Kunde.' },
        p1: { name: 'Dr. Nico Haberer', role: 'Gründer Energieglück Gruppe, Geschäftsführer Energieglück Batteriespeicher GmbH', desc: '17 Jahre Energiewirtschaft, 7 Jahre KI. Von der Vision zum Produkt.' }
      },
      companies: {
        title: 'Zwei Unternehmen, ein Ziel',
        bess: { name: 'Energieglück Batteriespeicher GmbH', desc: 'BESS-Entwicklung, Co-Location, Umspannwerke' },
        ai: { name: 'Energieglück AI GmbH', desc: 'Energieglück+, GridStable, KI-Agenten' }
      },
      structure: {
        title: 'Langfristig aufgestellt',
        foundation: 'Haberer Familienstiftung',
        holding: 'Energieglück GmbH (Holding)',
        bess: 'EG Batteriespeicher GmbH',
        ai: 'EG AI GmbH',
        spvs: 'Projekt-SPVs',
        claim: 'Unabhängig. Langfristig. Generationendenken statt Quartalsdenken.'
      },
      reference: {
        quote: 'Energieglück+ ermöglicht uns ein intelligentes, effizientes Anlagenmanagement – und die Kommunikation ist jederzeit transparent, lösungsorientiert und partnerschaftlich.',
        author: 'Bernd Schill',
        company: 'Leiter Erneuerbare Energien, Stadtwerke Göttingen AG'
      },
      mission: {
        title: 'Unsere Mission',
        text: 'Wir bauen Batteriespeicher und entwickeln KI-Lösungen, die die Energiewende intelligenter machen. Infrastruktur und Software aus einer Hand — für stabile Netze, optimierte Prozesse und eine bessere Zukunft für unsere Kinder.'
      },
      vision: {
        title: 'Unsere Vision',
        text: 'Speicher und KI werden selbstverständlicher Teil eines verlässlichen, sauberen Energiesystems. Nicht als Versprechen für irgendwann, sondern als Technologie, die heute funktioniert — und die Welt für kommende Generationen besser hinterlässt.'
      },
      values: {
        title: 'Unsere Werte',
        kiFirst: { title: 'KI-First', desc: 'Jede Entscheidung, jeder Prozess — datenbasiert und KI-gestützt.' },
        precision: { title: 'Präzision', desc: 'Entscheidungen auf Basis belastbarer Modelle, nicht Bauchgefühl.' },
        automation: { title: 'Automatisierung', desc: 'Was automatisiert werden kann, wird automatisiert.' },
        sustainability: { title: 'Nachhaltigkeit', desc: 'Wirtschaftlichkeit und Energiewende zusammen denken.' }
      }
    },
    projects: {
      title: 'Unsere Projekte',
      subtitle: 'Batteriespeicher-Projekte von der Entwicklung bis zum Betrieb',
      featured: {
        badge: 'Referenzprojekt',
        power: 'Leistung',
        capacity: 'Kapazität',
        inverter: 'Wechselrichter',
        battery: 'Batteriespeicher',
        statusLabel: 'Status',
        statusValue: 'Inbetriebnahme geplant Ende 2026',
        description: 'Von Energieglück+ gemanagt — unser KI Asset Management im Einsatz.'
      },
      pipeline: {
        title: 'Auszug aus unserer Pipeline',
        subtitle: 'Weitere Standorte in Planung und Entwicklung',
        colLocation: 'Standort',
        colPower: 'Leistung',
        colCapacity: 'Kapazität',
        colStatus: 'Status',
        statusDev: 'In Entwicklung',
        badgeLarge: 'Großprojekt',
        nrw: 'Nordrhein-Westfalen',
        rlp: 'Rheinland-Pfalz',
        sachsen1: 'Sachsen',
        sachsen2: 'Sachsen',
        sachsenAnhalt1: 'Sachsen-Anhalt',
        sachsenAnhalt2: 'Sachsen-Anhalt',
        thueringen: 'Thüringen'
      }
    },
    careers: {
      meta: {
        title: 'Karriere - Energieglück',
        description: 'Gestalte mit uns die Energie der Zukunft. Werde Teil des Energieglück-Teams und arbeite an Batteriespeichern, GridStable-Software und der Energieglück+ Plattform.',
      },
      hero: {
        welcome: 'Karriere',
        title: 'Wir bauen Speicher und KI. Du auch?',
        description: 'Energieglück wächst. Wir suchen Leute, die Batteriespeicher-Projekte umsetzen, KI-Software entwickeln und mit uns die Energiewirtschaft verändern. Remote-first, Saarland-based, kurze Wege.',
        imageAlt: 'Team bei Energieglück',
      },
      benefits: {
        title: 'Was dich erwartet',
        subtitle: 'Kein Konzern, kein Startup-Chaos — ein Unternehmen, das liefert',
        meaningful: {
          title: 'Echte Projekte',
          description: '2 GW+ Pipeline, KI im Echtbetrieb bei Stadtwerken — nicht auf dem Papier',
        },
        innovation: {
          title: 'KI-First',
          description: 'Wir nutzen KI in jedem Prozess — intern und für Kunden. Du arbeitest mit den neuesten Modellen',
        },
        team: {
          title: 'Kleines Team, große Wirkung',
          description: 'Flache Hierarchien, direkte Kommunikation, schnelle Entscheidungen',
        },
      },
      jobs: {
        title: 'Offene Stellen',
        subtitle: 'Aktuell suchen wir Verstärkung in diesen Bereichen',
      },
      application: {
        title: 'Initiativbewerbung',
        description: 'Keine passende Stelle? Schreib uns trotzdem — wir wachsen schnell',
        button: 'Initiativbewerbung senden',
        emailSubject: 'Initiativbewerbung',
      },
      contact: {
        text: 'Fragen zur Bewerbung? Schreibe uns an',
      },
    },
    press: {
      eyebrow: 'Newsroom',
      title: 'News & Presse',
      subtitle: 'Pressemitteilungen, Projekt-Updates und Hintergründe rund um Energieglück.',
      source: 'Quelle:',
      original: 'Originalartikel',
      empty: 'Derzeit sind keine Pressemeldungen verfügbar.',
      readMore: 'Weiterlesen',
      backToOverview: 'Zurück zu News & Presse',
      notFound: 'Artikel nicht gefunden',
      publishedAt: 'Erschienen bei:',
      filter: {
        all: 'Alle',
      },
      contact: {
        title: 'Pressekontakt',
        text: 'Für Presseanfragen stehen wir Ihnen gerne zur Verfügung',
        name: 'Marny Meyer',
        email: 'presse@energieglueck.de'
      },
      categories: {
        unternehmen: 'Unternehmen',
        projekt: 'Projekt',
        technologie: 'Technologie'
      }
    },
    career: {
      meta: {
        title: 'Karriere - Energieglück',
        description: 'Gestalte mit uns die Energie der Zukunft. Werde Teil des Energieglück-Teams und arbeite an Batteriespeichern, GridStable-Software und der Energieglück+ Plattform.'
      },
      hero: {
        badge: 'Careers',
        title: 'We build storage and AI. You too?',
        text: 'Energieglück is growing. We are looking for people to implement battery storage projects, develop AI software and change the energy industry with us. Remote-first, based in Saarland, short paths.'
      },
      why: {
        title: 'What to expect',
        subtitle: 'Not a corporation, not startup chaos — a company that delivers',
        meaningful: { title: 'Real Projects', text: '2 GW+ pipeline, AI in live operation at utilities — not just on paper' },
        innovation: { title: 'AI-First', text: 'We use AI in every process — internally and for customers. You work with the latest models' },
        team: { title: 'Small Team, Big Impact', text: 'Flat hierarchies, direct communication, fast decisions' }
      },
      jobs: {
        title: 'Open Positions',
        subtitle: 'We are currently looking for reinforcement in these areas',
        initiative: {
          title: 'Speculative Application',
          text: 'No matching position? Write us anyway — we are growing fast',
          button: 'Initiativbewerbung senden'
        },
        questions: 'Fragen zur Bewerbung? Schreibe uns an'
      }
    },
    contact: {
      title: 'Kontakt',
      subtitle: 'Haben Sie Fragen? Wir sind für Sie da!',
      form: {
        title: 'Kontaktformular',
        firstname: 'Vorname',
        lastname: 'Name',
        email: 'E-Mail',
        phone: 'Telefon',
        subject: 'Betreff',
        message: 'Nachricht',
        placeholder: 'Ihre Nachricht an uns...',
        submit: 'Absenden',
        submitting: 'Wird gesendet...',
        selectSubject: 'Bitte wählen...',
        subjects: {
          landowner: 'Flächeneigentümer',
          gridoperator: 'Netzbetreiber',
          municipality: 'Kommune',
          demo: 'Energieglück+ Demo',
          press: 'Presse',
          other: 'Sonstiges'
        },
        success: {
          title: 'Nachricht gesendet!',
          text: 'Vielen Dank für Ihre Nachricht! Wir melden uns schnellstmöglich bei Ihnen.'
        },
        error: {
          title: 'Fehler beim Versenden',
          text: 'Ihre Nachricht konnte nicht versendet werden. Bitte versuchen Sie es später erneut.'
        }
      },
      info: {
        title: 'Kontaktdaten',
        company: 'Energieglück Gruppe',
        demo: {
          title: 'Interesse an Energieglück+?',
          text: 'Erleben Sie unsere KI-gestützte Asset Management Software in einer persönlichen Demo',
          button: 'Jetzt Demo vereinbaren'
        }
      }
    },
    imprint: {
      meta: {
        title: 'Impressum | Energieglück Gruppe',
        description: 'Impressum der Energieglück Gruppe - Rechtliche Informationen und Kontaktdaten des Unternehmens.'
      },
      title: 'Impressum',
      company: {
        title: 'Angaben gemäß § 5 DDG (ehemals § 5 TMG)',
        name: 'Energieglück Gruppe'
      },
      representative: {
        title: 'Vertreten durch',
        name: 'Dr. Nico Haberer'
      },
      register: {
        title: 'Registereintrag',
        entry: 'Eintragung im Handelsregister.',
        court: 'Registergericht: Amtsgericht Saarbrücken',
        number: 'Registernummer: HRB 111679'
      },
      contact: {
        title: 'Kontakt',
        email: 'E-Mail: info@energieglueck.de'
      },
      responsible: {
        title: 'Inhaltlich verantwortlich gemäß § 18 Abs. 2 MStV',
        name: 'Dr. Nico Haberer'
      },
      dispute: {
        title: 'Verbraucherstreitbeilegung / Universalschlichtungsstelle',
        text: 'Wir nehmen nicht an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teil und sind dazu auch nicht verpflichtet.'
      },
      liability: {
        title: 'Haftungsausschluss',
        content: {
          title: 'Haftung für Inhalte',
          text: 'Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs.1 DDG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.'
        },
        links: {
          title: 'Haftung für Links',
          text: 'Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.'
        },
        copyright: {
          title: 'Urheberrecht',
          text: 'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.'
        }
      },
      privacy: {
        title: 'Datenschutz',
        gdpr: 'Datenschutzerklärung nach DSGVO',
        intro: 'Wir freuen uns sehr über Ihr Interesse an unserem Unternehmen. Datenschutz hat einen besonders hohen Stellenwert für die Geschäftsleitung der Energieglück Gruppe.',
        responsible: {
          title: 'Verantwortlicher',
          company: 'Energieglück Gruppe',
          representative: 'Vertreten durch: Dr. Nico Haberer'
        },
        collection: {
          title: 'Erhebung und Verarbeitung personenbezogener Daten',
          intro: 'Beim Besuch unserer Website werden folgende Daten gespeichert:',
          data1: 'IP-Adresse',
          data2: 'Datum und Uhrzeit des Zugriffs',
          data3: 'Verwendeter Browser',
          data4: 'Betriebssystem',
          data5: 'Referrer-URL',
          outro: 'Diese technischen Daten sind zur Darstellung und Sicherheit der Website erforderlich und werden in Server-Logfiles gespeichert.'
        },
        contact: {
          title: 'Kontaktformular & Kommunikation',
          text: 'Wenn Sie uns per Kontaktformular oder E-Mail Anfragen zukommen lassen, werden Ihre Angaben zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.'
        },
        cookies: {
          title: 'Cookies und Analyse',
          text: 'Unsere Website verwendet Cookies. Details siehe Cookie-Richtlinie. Cookies dienen der besseren Nutzbarkeit und ggf. zur Webanalyse.'
        },
        rights: {
          title: 'Rechte der Nutzer',
          text: 'Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Widerspruch und Datenübertragbarkeit. Wenden Sie sich für diesbezügliche Anliegen bitte jederzeit an info@energieglueck.de.'
        },
        complaint: {
          title: 'Beschwerderecht',
          text: 'Es besteht ein Beschwerderecht bei der zuständigen Datenschutz-Aufsichtsbehörde, z.B. LfDI Saarland.'
        },
        thirdparty: {
          title: 'Dienste Dritter',
          text: 'Falls Google Analytics, Social Media Plugins, oder andere externe Dienste eingesetzt werden, muss dies hier zusätzlich individuell angegeben werden.'
        },
        changes: {
          title: 'Änderung der Datenschutzerklärung',
          text: 'Diese Erklärung wird bei gesetzlichen Änderungen oder Anpassungen der Website aktualisiert.'
        }
      }
    },
    whitepaper: {
      meta: {
        title: 'Whitepaper | Energieglück',
        description: 'Kostenlose Analysen, Studien und technische Berichte für Fachleute aus der Energiewirtschaft.'
      },
      hero: {
        badge: 'Expertenwissen',
        title: 'Analysen. Studien. Expertenwissen.',
        subtitle: 'Fundierte Entscheidungsgrundlagen für Energieprofis — kostenlos.'
      },
      authors: 'Autoren',
      publisher: 'Herausgeber: Dr. Nico Haberer, Energieglück Gruppe',
      downloadFree: 'Kostenlos herunterladen',
      notifyMe: 'Interesse melden',
      coverLabel: 'Whitepaper',
      comingSoonBadge: 'Demnächst',
      pages: 'Seiten',
      readingTime: 'Min. Lesezeit',
      upcoming: {
        title: 'In Vorbereitung',
        subtitle: 'An diesen Studien arbeiten wir aktuell.'
      },
      cta: {
        title: 'Individuelle Analyse für Ihr Projekt?',
        subtitle: 'Unsere Experten beraten Sie gerne zu BESS, Hybrid-Energieparks und intelligenter Betriebsführung.',
        button: 'Beratung anfragen'
      }
    }
  },
  en: {
    journey: {
      aiLabel: {
        short: 'Visualisation · AI-generated'
      },
      hero: {
        brand: 'ENERGIEGLÜCK',
        headline: 'Large-scale storage. And the AI behind it.',
        subline: '',
        scroll: 'Scroll',
        swipe: 'Swipe'
      },
      problem: {
        eyebrow: 'The problem',
        p1: 'Plants get built. And then only watched.',
        p2: 'Dashboards display. Nobody acts.',
        p3: 'Grid, revenue, ageing. Three goals that contradict each other.'
      },
      how: {
        eyebrow: 'How it works',
        s1: {
          title: 'Real plant',
          text: 'A large-scale storage system. Developed, built and operated by Energieglück.'
        },
        s2: {
          title: 'Digital twin',
          text: 'A physics-based model of the plant. Knows every second what it should be doing.'
        },
        s3: {
          title: 'AI agents',
          text: 'They act. At the storage, in the grid, in the company.'
        }
      },
      landing: {
        eyebrow: 'Energieglück',
        headline: 'AI agents that act.',
        agents: {
          net: {
            title: 'Grid agent',
            text: 'Detects redispatch and connection limits, operates the storage in compliance, documents automatically.'
          },
          revenue: {
            title: 'Revenue agent',
            text: 'Weighs balancing power against arbitrage, subtracts ageing costs, picks the best schedule.'
          },
          state: {
            title: 'Condition agent',
            text: 'Keeps reserve and SoC windows, monitors cell ageing against the warranty curve.'
          },
          forecast: {
            title: 'Forecast agent',
            text: 'Combines weather, market and plant condition into a schedule preview for the next 48 hours.'
          },
          service: {
            title: 'Service agent',
            text: 'Spots faults in the plant topology, opens the ticket, directs the service partner.'
          },
          more: {
            title: 'And in your company.',
            text: 'Customer service that answers. Documents that sort themselves. Projects that warn before it gets expensive.',
            link: 'More on AI at Energieglück'
          }
        }
      },
      after: {
        text: 'Energieglück develops, builds and operates large-scale storage. We built the AI that runs it ourselves. The same agents work today for utilities and energy suppliers: in processes, documents and customer communication.',
        cta: 'Request a conversation'
      }
    },
    assetMgmt: {
      meta: {
        title: 'AI Asset Management for PV & Battery Storage | Energieglück+',
        description: 'Digital twin, predictive maintenance and real-time control for PV and BESS portfolios. +15% yield gain, -40% downtime.'
      },
      hero: {
        title: 'No fire risk. No surprises. No revenue losses.',
        subtitle: 'AI Asset Management for PV and BESS portfolios.'
      },
      stats: {
        yield: 'Yield Gain',
        downtime: 'Downtime',
        intraday: 'Intraday Profit'
      },
      areas: {
        title: 'Two Worlds. One Platform.',
        moreLink: 'More on Energieglück+',
        pv: {
          title: 'PV Systems',
          f1: 'Digital twin with physics-based setpoint calculation',
          f2: 'AI forecasting with 96% accuracy',
          f3: 'Proactive maintenance before visible failures',
          f4: 'Monetary loss assessment in real-time',
          f5: 'Redispatch optimization and intraday trading'
        },
        bess: {
          title: 'Battery Storage',
          f0: 'Digital Twin for BESS — from rack to cell level',
          f1: 'SOH and RUL monitoring at cell level',
          f2: 'Thermal early detection and SOC optimization',
          f3: 'SOC drift correction and degradation analysis',
          f4: 'Insurance discount for BESS operators'
        }
      },
      features: {
        title: 'Features',
        card1: { title: 'Predictive Fault Diagnosis', text: 'AI detects anomalies across all topology levels — before they become failures.' },
        card2: { title: 'Intelligent Redispatch Management', text: 'Automated dispatch planning and grid congestion management for PV and BESS.' },
        card3: { title: 'Predictive Maintenance', text: 'Data-driven maintenance planning reduces costs and maximizes availability.' },
        card4: { title: 'Real-Time Control', text: 'Direct access to inverters and BMS — interventions in seconds, not hours.' }
      },
      usp: {
        title: '24/7 AI Monitoring + Personal Support',
        subtitle: 'Automation where it makes sense. Experienced asset managers when it matters. Your personal control center.'
      },
      caseStudy: {
        title: '260+ MWp analyzed. 1.5M EUR in annual losses identified.',
        subtitle: 'Our AI systematically identified hidden yield losses in existing portfolios — from shading to degradation to faulty string configurations.'
      },
      cta: {
        title: 'See for yourself what your portfolio holds.',
        button: 'Book Demo'
      }
    },
    projektmgmt: {
      meta: {
        title: 'AI Project Management for energy projects | Energieglück',
        description: 'AI across all project documents, an early-warning system for risks, and one system across the full lifecycle — from scouting to operation. Proven on Germany\'s largest storage project.'
      },
      hero: {
        eyebrow: 'AI Project Management',
        title: 'Project management that thinks ahead.',
        subtitle: 'AI across all project documents, an early-warning system for risks — proven on Germany\'s largest storage project.',
        cta1: 'Request a demo',
        cta2: 'More AI solutions'
      },
      areas: {
        eyebrow: 'What it does',
        title: 'Three building blocks for energy projects.',
        rag: {
          title: 'AI across all project documents',
          f1: 'Ask contracts, expert reports, minutes and authority correspondence in natural language.',
          f2: 'Automatic document ingest from SharePoint, Paperless and meeting transcripts.',
          f3: 'Answers with source references — no digging through folder trees.'
        },
        warn: {
          title: 'Early-warning system for project risks',
          f1: 'Deadline, permitting and grid-connection risks are detected proactively.',
          f2: 'Instead of a reactive status report: a heads-up before a bottleneck escalates.',
          f3: 'Prioritised risk overview across the entire project portfolio.'
        },
        lifecycle: {
          title: 'From scouting to operation',
          f1: 'One system across the full lifecycle: acquisition, active project phase, closeout.',
          f2: 'Tasks, checklist templates and a central contact database (grid operators, authorities, owners).',
          f3: 'Public project status page for transparent stakeholder communication.'
        }
      },
      caps: {
        eyebrow: 'Under the hood',
        title: 'Built AI-native.',
        c1: { title: 'Document RAG', text: 'Retrieval-augmented generation across the complete project archive — with source attribution.' },
        c2: { title: 'Early-warning engine', text: 'Rule- and data-based risk detection across all running projects.' },
        c3: { title: 'Open AI integration', text: 'Connect your own AI chatbots or assistants to query and maintain project data in natural language — via an open interface (MCP standard).' },
        c4: { title: 'Statistics & reporting', text: 'Portfolio statistics, finances and quotes in one place.' }
      },
      usp: {
        title: 'Proven on Germany\'s largest storage project.',
        subtitle: 'In production across a 2 GW+ project pipeline and steering Germany\'s largest storage by project size — not a concept, but lived practice.'
      },
      faq: {
        eyebrow: 'FAQ',
        title: 'Answers at a glance.',
        q1: 'Which projects is this for?',
        a1: 'Energy projects of all kinds — PV, battery storage, grid — from acquisition to operation.',
        q2: 'Where do the documents for the AI come from?',
        a2: "From all of the company's distributed data sources; ingest runs automatically.",
        q3: 'How does the early-warning system work?',
        a3: 'It detects deadline, permitting and grid-connection risks proactively and prioritises them across the portfolio.',
        q4: 'Can I connect my own AI assistants?',
        a4: 'Yes — via an open interface (MCP standard) you connect your own AI chatbots or assistants to query and maintain project data in natural language.'
      },
      cta: {
        title: 'See it on your project portfolio.',
        button: 'Request a demo'
      }
    },
    ai: {
      nav: {
        title: 'AI',
        overview: 'Overview',
        reachy: 'AI Assistant',
        projektmanagement: 'Project Management',
        useCases: 'Use Cases',
        consulting: 'Consulting',
        playground: 'AI Playground'
      },
      reachy: {
        meta: {
          title: 'Reachy — AI Assistant for Businesses | Energieglück AI',
          description: 'Reachy is the AI employee that answers emails, takes calls, and solves problems — 24/7, GDPR-compliant, Made in Germany.'
        },
        hero: {
          badge: 'NEW',
          title: 'The AI employee that never sleeps',
          subtitle: 'Imagine: An employee who simultaneously answers emails, takes calls, manages your chat, and responds on social media – error-free, 24/7, without vacation.',
          hook: 'Reachy is not a chatbot. He thinks along. He recognizes when something goes wrong and acts – before you notice.'
        },
        features: {
          title: 'What makes Reachy special?',
          card1: { title: 'All Communication Channels', text: 'Email, chat, phone, social media – one assistant for everything' },
          card2: { title: 'Full Data Access', text: 'Secure connection to all company data in real-time' },
          card3: { title: 'Proactive Work', text: 'Recognizes need for action and acts independently' },
          card4: { title: 'Multi-Agent Teams', text: 'Spawns specialized AI colleagues for complex tasks' },
          card5: { title: 'Enterprise Security', text: 'Made in Germany – GDPR-compliant and auditable' },
          card6: { title: 'Self-Learning', text: 'Becomes smarter and more efficient with each use' }
        },
        trust: {
          badge1: 'GDPR-compliant',
          badge2: 'Made in Germany',
          badge3: 'Enterprise-ready'
        },
        cta: {
          title: 'Seeing is believing. Free live demo.',
          subtitle: '30 Minutes · No sales pitch',
          button: 'Book Demo'
        }
      },
      assetManagement: {
        meta: {
          title: 'AI Asset Management for PV & Battery Storage | Energieglück+',
          description: '96% forecast accuracy, 98% availability. Energieglück+ is the AI platform for PV and BESS asset management with digital twin and predictive maintenance.'
        },
        hero: {
          title: 'Others monitor. We manage.',
          subtitle: 'Other systems show colorful dashboards. We tell you where you are losing money right now – and why.'
        },
        keyNumbers: {
          title: 'The numbers speak for themselves',
          accuracy: '96% Forecast Accuracy',
          availability: '98% Plant Availability',
          comparison: '15 min Target-Actual Comparison',
          monitoring: '24/7 AI Monitoring + Control Center'
        },
        digitalTwin: {
          title: 'Digital Twin – The Core',
          subtitle: 'Physical-technical setpoint calculation instead of statistical component comparisons. The only system for objective identification of underperformance – even without visible failures.',
          feature1: 'Module characteristics & meteorological measurements',
          feature2: 'Loss calculations (shading, cables)',
          feature3: 'Monetary loss assessment in real-time',
          feature4: 'Predictive maintenance before failures'
        },
        features: {
          title: 'All features at a glance',
          card1: { title: 'Performance Analysis', text: 'Identification of underperformance at all topology levels' },
          card2: { title: 'Automated Reporting', text: 'PAC/FAC tests, PR guarantee documentation' },
          card3: { title: 'BESS Management', text: 'SoC/SoH monitoring, degradation analysis' },
          card4: { title: 'Human + Machine', text: 'AI 24/7 + own control center with asset managers' }
        },
        cta: {
          title: 'Your power plant runs itself.',
          button: 'Learn More'
        }
      },
      useCases: {
        meta: {
          title: 'AI Use Cases for Businesses — Ready to Deploy | Energieglück AI',
          description: '10 AI use cases for your business. From intent recognition to churn prediction — we integrate AI into your existing processes.'
        },
        hero: {
          title: 'AI Use Cases for Utilities and Energy Suppliers',
          subtitle: 'Pragmatic, measurable, and ready to deploy — from automated customer communication to intelligent knowledge bases.'
        },
        intro: {
          text: 'Staff shortages, rising service expectations, and growing document volumes — utilities and energy suppliers face real challenges. Our AI solutions address exactly these issues: field-tested, GDPR-compliant, and integrable into existing IT landscapes.',
          discoverButton: 'Discover use cases',
          playgroundButton: 'Try the Playground'
        },
        clusters: {
          customerService: {
            title: 'Customer Service & Sales'
          },
          processes: {
            title: 'Processes & Documents'
          },
          knowledge: {
            title: 'Knowledge & AI Agents'
          }
        },
        playgroundBanner: {
          title: 'Try it yourself',
          text: 'Test our AI tools in a protected demo environment — free and without obligation.',
          button: 'Go to AI Playground'
        },
        cases: {
          title: 'The 10 most important use cases',
          case1: { 
            title: 'Intent Recognition', 
            problem: 'Emails end up in the wrong inbox. Customers wait for days.',
            text: 'Our AI classifies every incoming message in under 2 seconds — and routes it directly to the right contact person.',
            metric: '60-90% faster processing',
            before: '15 min manual sorting',
            after: '2 sec automatic'
          },
          case2: { 
            title: 'Data Extraction', 
            problem: 'Hours wasted typing from PDFs and forms.',
            text: 'AI reads every document, extracts all important data and automatically enters it into the right system — even with handwritten notes.',
            metric: '40% less manual work',
            before: '20 min/document',
            after: '30 sec/document'
          },
          case3: { 
            title: 'AI Responses', 
            problem: 'Standard questions cost time. Customers wait for simple answers.',
            text: 'AI knows your best answers and personalizes them for each customer — with your tone, your products, in perfect quality.',
            metric: 'Personalized, quality-checked',
            before: '45 min research',
            after: '30 sec perfect answer'
          },
          case4: { 
            title: 'Sentiment Analysis', 
            problem: 'Upset customers are often recognized too late.',
            text: 'Every message is analyzed for emotions. Critical cases immediately go to the boss — before the customer switches to the competition.',
            metric: 'Automatic prioritization',
            before: 'Dissatisfaction unnoticed',
            after: 'Instant alert for problems'
          },
          case5: { 
            title: 'Segmentation', 
            problem: 'All customers receive the same treatment.',
            text: 'AI recognizes VIP customers, new customers and problem cases automatically — and adapts communication and offers accordingly.',
            metric: 'By behavior & value',
            before: 'One-size-fits-all',
            after: 'Individual treatment'
          },
          case6: { 
            title: 'Churn Prediction', 
            problem: 'Cancellations come as a surprise.',
            text: 'The system recognizes early warning signs: less activity, negative language, support clustering. You can react before it\'s too late.',
            metric: 'Warning before cancellation',
            before: 'Reactive damage control',
            after: 'Proactive customer rescue'
          },
          case7: { 
            title: 'Personalization', 
            problem: 'Offers miss the mark. Timing is bad.',
            text: 'AI recognizes the perfect moment: Customer is currently interested in additional products? Automatic, natural cross-selling.',
            metric: 'Natural cross-selling',
            before: 'Random offers',
            after: 'Perfect timing'
          },
          case8: { 
            title: 'Processing Time', 
            problem: 'Complex inquiries take forever.',
            text: 'AI handles research, data collection and preparation. Your team focuses on decisions and customer contact.',
            metric: '90% time savings',
            before: '15 min/inquiry',
            after: '1.5 min/inquiry'
          },
          case9: { 
            title: 'OCR & Documents', 
            problem: 'Manually typing from document photos.',
            text: 'Simply take a phone photo. AI recognizes all texts, numbers and data — regardless of crooked photos, handwritten or wrinkled.',
            metric: 'No matter how crooked',
            before: 'Manual typing',
            after: '3 sec in system'
          },
          case10: { 
            title: 'Knowledge Base', 
            problem: 'Company know-how is trapped in heads.',
            text: 'AI learns from all emails, documents and conversations. Every employee has instant access to the entire company knowledge.',
            metric: 'Learns, never forgets',
            before: 'Knowledge in silos',
            after: 'Collective intelligence'
          }
        },
        socialProof: {
          title: '',
          stat1: '10 Use Cases ready to deploy',
          stat2: '0 days setup for standard solutions',
          stat3: '100% GDPR — hosted in Germany',
          stat4: '∞ scalable, always learning',
          quote: '"AI is our DNA — we don\'t just talk about it, we live it. Every day, in every process."',
          author: 'Dr. Nico Haberer, Founder Energieglück Group'
        },
        comparison: {
          title: 'Before vs. After: The AI Difference',
          subtitle: 'Concrete numbers from real projects',
          tableHeaders: {
            process: 'Process',
            manual: 'Manual',
            withAI: 'With AI'
          },
          processes: [
            {
              name: 'Email sorting',
              manual: '15 min/mail',
              ai: '2 sec/mail'
            },
            {
              name: 'Document extraction',
              manual: '20 min/document',
              ai: '30 sec/document'
            },
            {
              name: 'Grid impact analysis',
              manual: '200 hours',
              ai: '20 hours'
            },
            {
              name: 'Data entry error rate',
              manual: '8-12%',
              ai: 'under 0.5%'
            },
            {
              name: 'Support availability',
              manual: 'Mon-Fri 9-17',
              ai: '24/7'
            }
          ]
        },
        faq: {
          title: 'Frequently Asked Questions',
          questions: [
            {
              question: 'Which use case fits my company?',
              answer: 'It depends on your biggest time wasters. Email-heavy companies benefit from intent recognition and AI responses. Document-heavy industries love OCR and data extraction. In a 30-minute conversation, we find the 3 most important use cases for you.'
            },
            {
              question: 'How long does integration take?',
              answer: 'The first use cases are productive in 2-4 weeks. More complex integrations take 6-12 weeks. We always start with the biggest quick win and then gradually expand.'
            },
            {
              question: 'Is the AI GDPR-compliant?',
              answer: '100% yes. All data remains in Germany, is processed encrypted and can be deleted at any time. We only work with GDPR-certified partners and conduct regular audits.'
            },
            {
              question: 'How does integration into our processes work?',
              answer: 'We integrate our AI solutions individually into your existing or new processes — no off-the-shelf product. Whether email, CRM, ERP, or telephony: we adapt to your infrastructure, not the other way around.'
            },
            {
              question: 'What does it cost?',
              answer: 'Costs depend on the number of use cases and users. A typical setup for 10-50 employees starts at €800/month. This usually pays for itself through the time savings of the first week. Exact prices after the free consultation.'
            }
          ]
        },
        cta: {
          title: 'Book a free consultation',
          subtitle: 'In 30 minutes we identify the most impactful AI levers for your utility — no strings attached.',
          button: 'Schedule appointment'
        }
      }
    },
    nav: {
      home: 'Home',
      energieglueck: 'Energieglück+',
      batteriespeicher: 'Battery Storage',
      gridstable: 'GridStable',
      flaecheneigentuemer: 'Land Owners',
      netzbetreiber: 'Grid Operators',
      kommunen: 'Municipalities',
      company: 'Company',
      aboutUs: 'About Us',
      about: 'About Us',
      projects: 'Projects & References',
      press: 'News & Press',
      career: 'Career',
      imprint: 'Imprint',
      contact: 'Contact',
      playground: 'AI Live Demo',
      knowledge: 'Knowledge',
      whitepaper: 'Whitepaper',
      useCases: 'Use Cases'
    },
    plattform: {
      nav: {
        cta: "Book a demo",
        dd: {
          uebersicht: "Overview",
          anwendungen: "Applications",
          wirtschaftlichkeit: "Economics",
          referenzen: "References",
        },
      },
      hero: {
        eyebrow: "AI agents + Digital Twin for PV & battery storage",
        h1: "No dashboard. AI agents managing your plant.",
        lede: "Energieglück+ is a self-learning, physics-based digital twin of your PV and battery storage assets — with AI agents autonomously overseeing yield, degradation, safety and trading. You see the outcomes, not the dashboards.",
        cta1: "Book a demo",
        cta2: "View economics",
        trustLine: "GDPR-compliant · Made in Germany · Hosted in Germany",
      },
      kpi: {
        k1: "96 %",
        l1: "Forecast accuracy of our AI agents in production",
        note: "A value sustained in operation, methodology per case study — not a blanket marketing figure.",
      },
      uebersicht: {
        intro: {
          eyebrow: "What our AI agents deliver",
          h2: "One twin of your plant. Agents that manage it.",
          p: "The digital twin models every PV and storage asset on physics — down to string and cell level. AI agents act on it: yield, diagnostics, safety and trading. From classic monitoring (Essentials) to the agent suite (Pro) to fully managed operations by the Energieglück team (Full-Service).",
        },
        monitoring: {
          t: "Agents instead of dashboards",
          p: "Yield, degradation, safety and trading agents run 24/7 on the digital twin of your plant — on a 15-minute cycle, with concrete recommended actions instead of colourful tiles.",
        },
        teaser: {
          cta: "Learn more",
        },
        finalCta: {
          title: "See what's inside your portfolio.",
        },
      },
      anwendungen: {
        eyebrow: "Applications",
        h2: "PV plants and battery storage in one platform.",
        hero: {
          subtitle: "One digital twin for both plant types — from the established PV monitoring to the battery storage component with its own decision layer. AI forecasting instead of plain display.",
        },
        section: {
          h2: "Two worlds. One platform.",
        },
        forest: {
          h2: "PV and storage under one logic. Agents that act.",
          p: "The digital twin models PV plants and battery storage on physics. AI agents work on it — proactive maintenance, yield optimisation and trading decisions instead of reactive incident reports.",
        },
        pv: {
          t: "PV plants",
          p: "The established digital PV twin combines a 15-minute time series of actual data with an evaluation logic, surfacing losses that remain invisible in standard monitoring.",
          lead: "Yield agent + diagnostics agent — surface losses that standard monitoring misses.",
          c1: "Yield agent",
          c2: "Diagnostics agent",
          c3: "Hidden losses surfaced",
          c4: "API / data-logger connection",
          b1: "Digital twin with AI forecast: PV forecast and PV setpoint, weather service integrated.",
          b2: "Proactive maintenance and fault diagnosis instead of reactive incident reports.",
          b3: "Surfaces hidden losses: DC/AC overdimensioning, MPP tracking, shading, thermal losses — without blanket monetary figures.",
          b4: "Inverter connection via API or data logger.",
        },
        bess: {
          t: "Battery storage (BESS)",
          p: "The battery storage twin is physics-based: remaining useful life, manufacturer-independent SOH, safety and a decision layer for regulatorily and commercially relevant situations.",
          lead: "Safety, degradation and trading agent — protect lifetime, secure revenue, prove safety.",
          c1: "Degradation agent (SOH/RUL)",
          c2: "Safety agent (IEC 62619)",
          c3: "Self-learning twin",
          c4: "Trading agent",
          b1: "Remaining useful life (RUL) forecast: remaining service life per rack and site with confidence band and end-of-life date.",
          b2: "Manufacturer-independent SOH model: state of health calculated by us rather than taken from the BMS — objective, with warranty tracking.",
          b3: "Thermal runaway monitoring per IEC 62619: safety-relevant events aggregated and prioritised, with audit trail.",
          b4: "Self-learning digital twin: learns your plant's real cell behaviour and calibrates against drift.",
          b5: "Real-time anomaly detection: model↔measurement deviations are detected statistically before they cause an outage.",
          b6: "Decision layer: concrete recommended action for redispatch / §13a EnWG, negative prices, PCC limit and FCR conflicts.",
          b7: "Warranty and cycle budget: cycle consumption projected against the warranty contract — breach risk visible early.",
          b8: "Per-plant P&L: revenues (arbitrage / FCR / aFRR / redispatch) against degradation, HVAC and opportunity costs — plan vs. actual per month.",
        },
        tech: {
          t: "Technology",
          p: "Not a generic dashboard: Energieglück+ runs a self-learning, physics-based digital twin for PV plants and battery storage — for storage additionally remaining-useful-life and SOH forecasts with confidence band, an Arrhenius degradation model, IEC 62619 safety monitoring and statistical anomaly detection; for PV, forecast and hidden yield losses. This is the logic of leading asset-management research, in production. Data sources SCADA, data logger or API on a 15-minute cycle; hosting in Germany or the EU.",
        },
        andock: {
          eyebrow: "Integration",
          h2: "Keep your hardware. Our AI plugs in.",
          c1: {
            t: "No new hardware",
            p: "No CapEx, no site visit. We read what is already running on your side — PV inverters as well as battery BMS. You don't change a thing.",
          },
          c2: {
            t: "Vendor-independent",
            p: "Connects to FusionSolar, SMA, Fronius, Sungrow, Meteocontrol and MQTT. We compute battery SOH ourselves, physically — not taken from the BMS.",
          },
          c3: {
            t: "Live in minutes, not months",
            p: "All we need are credentials for inverter API, BMS or SCADA. AI-driven self-onboarding typically brings PV and storage assets into production within minutes.",
          },
        },
        finalCta: {
          title: "Ready for measurable operations management?",
        },
      },
      wirtschaft: {
        eyebrow: "Economics",
        h2: "We compute with physics, not with promises.",
        hero: {
          subtitle: "Hidden losses become visible, and accounting follows the formula in the customer contract — not a generic standard KPI, no blanket promises.",
        },
        value: {
          t: "How Energieglück+ creates value",
          p: "Monitoring shows states — AI agents act, and the digital twin makes it traceable. Essentials delivers classic monitoring. Pro starts the agent suite on the digital twin: yield, diagnostics, safety and decision agents forecast remaining useful life, SOH and performance with confidence bands and turn everything into euros. Full-Service adds trading and maintenance agents — supervised by the Energieglück team and accounted for exactly along the formula in your contract.",
        },
        tiers: {
          h2: "Three tiers, depending on the operating model.",
          anchorBadge: "ANCHOR",
          essentials: {
            t: "Essentials",
            for: "Simple monitoring.",
            i1: "Monitoring",
            i2: "Alerting",
            i3: "Standard reports",
            i4: "",
            i5: "",
            i6: "",
            footLabel: "",
            foot: "",
          },
          pro: {
            t: "Pro",
            for: "Advanced monitoring.",
            i1: "Everything in Essentials",
            i2: "AI agents",
            i3: "Forecast",
            i4: "Digital Twin",
            i5: "Predictive Maintenance",
            i6: "Audit export",
            footLabel: "",
            foot: "",
          },
          fullservice: {
            t: "Full-Service",
            for: "Asset-management-as-a-service — platform + personal support by asset managers.",
            i1: "Everything in Pro",
            i2: "Personal support by asset managers",
            i3: "Commercial asset management",
            i4: "Investor reporting",
            i5: "",
            i6: "",
            footLabel: "NOTE",
            foot: "No on-site O&M — partners only.",
          },
          volumeLabel: "VOLUME TIERS",
          volume: "Dedicated discount steps along the growth curve — talk to us for tier pricing.",
        },
        versicherung: {
          t: "Insurance",
          p: "In partnership with insurers: documented risk reduction as a basis for negotiation. No promise of premium reduction.",
        },
        foerderung: {
          head: "Your investment is eligible for funding",
          sub: "Numerous federal and state programmes support the digitalisation of energy assets.",
          note: "Funding amount depends on location, company size and programme availability.",
          rateLabel: "Funding",
          maxLabel: "Max.",
          p1: { name: "InnoBonus", region: "Saarland", rate: "up to 75 %", max: "€75,000" },
          p2: { name: "Digitalbonus", region: "Bavaria", rate: "up to 50 %", max: "€30,000" },
          p3: { name: "DIGI-Zuschuss", region: "Hesse", rate: "50 %", max: "€10,000" },
          p4: { name: "KfW ERP loan", region: "Nationwide", rate: "3 % repayment", max: "€200,000" },
        },
        finalCta: {
          title: "Let's talk about your portfolio.",
        },
      },
      ref: {
        eyebrow: "References",
        h2: "In live operation, real.",
        hero: {
          subtitle: "Municipal utilities, PV portfolios and operators running our AI agents on the digital twin in production — not pilot slides, but plants in daily use.",
        },
        section: {
          h2: "Customers who work with us.",
        },
        finalCta: {
          title: "Become the next reference.",
        },
        goettingen: {
          name: "Stadtwerke Göttingen",
          desc: "Municipal PV operation.",
          quote: "Die Zusammenarbeit mit der Energieglück AI GmbH und der Einsatz von Energieglück+ ist für die Stadtwerke Göttingen durchweg positiv. Die Plattform ermöglicht uns ein intelligentes, effizientes Anlagenmanagement.",
          person: "Bernd Schill",
          role: "Head of Renewable Energies, Stadtwerke Göttingen",
        },
        aream: {
          name: "Aream",
          desc: "PV portfolio across Germany, Italy and Spain.",
        },
        green4net: {
          name: "Green4Net",
          desc: "PV operations in Italy and Germany.",
        },
        trust: {
          dsgvo: "GDPR-compliant",
          madeingermany: "Made in Germany",
          hosting: "Hosted in Germany",
        },
      },
      icpfaq: {
        investoren: "Investors: due-diligence-grade yield and risk transparency.",
        stadtwerke: "Municipal utilities: regulatorily clean municipal operation of PV and storage.",
        projektierer: "Developers: seamless handover from construction into optimised operation.",
        faq: {
          q1: "Which plants does Energieglück+ support?",
          a1: "Utility-scale PV and battery storage plants from 5 MWh, with a digital twin down to cell level.",
          q2: "How does the data get into the platform?",
          a2: "Via SCADA, data logger or API on a 15-minute cycle; the inverter connection is via API or data logger.",
          q3: "Where is the data hosted?",
          a3: "GDPR-compliant in Germany or the EU. Made in Germany, hosted in Germany.",
          q4: "Who handles onboarding?",
          a4: "In Essentials and Pro the platform guides you through AI-assisted self-onboarding — only the access credentials for BMS, SCADA or API are needed; we accompany you on request. Full-Service is handled entirely by the Energieglück team including contract-exact KPI accounting.",
          q5: "How fast am I live?",
          a5: "Self-onboarding is usually completed within a few minutes once the access credentials are available.",
          q6: "What sets you apart from pure monitoring?",
          a6: "Monitoring shows states. Energieglück+ runs a physics-based digital twin with AI agents on top — forecasting and giving concrete recommended actions up to operations management.",
          q7: "How do you calculate state of health (SOH)?",
          a7: "Manufacturer-independent and calculated by us — objective rather than taken from the BMS; including warranty tracking.",
          q8: "Can I change tier later?",
          a8: "Yes. Essentials → Pro → Full-Service is possible at any time, without data migration.",
          q9: "What happens to my data if I cancel?",
          a9: "Full data export, no lock-in mechanisms.",
          q10: "Do you support multi-plant portfolios from different operators?",
          a10: "Yes, tenant-separated with strict tenant isolation.",
        },
      },
      faq: {
        eyebrow: "FAQ",
        h2: "Answers at a glance.",
      },
    },
    hero: {
      slide1: {
        badge: 'AI Asset Management',
        subtitle: 'AI agents managing your PV and battery storage assets on a self-learning, physics-based digital twin.',
        feature1: 'Digital Twin',
        feature2: 'AI-driven Process Automation',
        feature3: 'Day-Ahead Forecasting',
        feature4: 'Predictive Maintenance',
        feature5: 'Real-time Monitoring & Optimization',
        cta: 'To the Product'
      },
      slide2: {
        title: 'Battery Storage as Grid Support',
        subtitle: 'With GridStable, we transform battery storage from pure profit maximizers to active grid supporters – for a stable and profitable energy future.',
        pipeline: '2GW+ Pipeline',
        development: 'Development, Construction & Operation',
        cta1: 'Discover GridStable',
        cta2: 'View Projects'
      },
      slide3: {
        badge: 'NEW',
        title: 'Your customers are waiting. Your AI responds.',
        subtitle: 'While others are still sorting emails, your AI has already responded, recognized the customer, and solved the problem.',
        cta: 'Discover AI'
      },
      stats: {
        pipeline: 'Battery Storage',
        technology: 'Technology',
        ai: 'AI Asset Management'
      }
    },
    home: {
      what: {
        eyebrow: 'What we do',
        title: 'Three things. AI is the foundation of all of them.',
        card1: {
          eyebrow: 'AI — our DNA',
          title: 'Own models, agents & automation',
          desc: 'From email classification to OCR to intent detection — AI runs through everything we do.',
          cta: 'AI services'
        },
        card2: {
          eyebrow: 'Project development',
          title: 'Large-scale battery storage',
          desc: 'BESS from 5 to 700 MW in Germany — AI-driven from scouting to RTB.',
          cta: 'GridStable™'
        },
        card3: {
          eyebrow: 'Energieglück+',
          title: 'AI agents + Digital Twin for PV & BESS',
          desc: 'Yield, diagnostics, safety and trading agents act on a physics-based twin of your plant — not on colourful dashboards.',
          cta: 'Request demo'
        }
      },
      who: {
        eyebrow: 'Who we work with',
        title: 'Utilities, grid operators, IPPs — and the people behind them.',
        card1: {
          title: 'Landowners',
          kpiValue: '20+',
          kpiLabel: 'years of lease',
          desc: 'Long-term, predictable income on areas from approx. 1 hectare — no costs, no risks.',
          cta: 'Learn more'
        },
        card2: {
          title: 'Grid operators',
          kpiValue: '24/7',
          kpiLabel: 'grid stabilization',
          desc: 'Seamless integration into existing grid infrastructure. Compliant with VDE-AR-N, TAR and EEG.',
          cta: 'Learn more'
        },
        card3: {
          title: 'Municipalities',
          kpiValue: '90%',
          kpiLabel: 'trade tax local',
          desc: 'Since the 2024 tax act, 90 % of trade tax stays on-site. A leading role in the energy transition.',
          cta: 'Learn more'
        }
      },
      join: {
        title: 'Become part of the energy transition.',
        subtitle: 'Whether you bring land, a grid, a municipality or an industry partnership — let us talk about making energy meaningful, together.',
        ctaPrimary: 'Get in touch',
        ctaSecondary: 'About us',
        trust: 'Reply within 24 h · info@energieglueck.de'
      }
    },
    index: {
      valueTitle: 'Your Added Value with Energieglück',
      valueSubtitle: 'We create real value for our partners – with innovative solutions for battery storage and PV systems',
      operators: {
        title: 'For Park Operators',
        subtitle: 'Energieglück+ enhances your plant performance',
        feature1: 'AI-based asset management with digital twin',
        feature2: 'Predictive maintenance reduces downtime',
        feature3: 'Real-time monitoring & day-ahead forecasting',
        feature4: 'Maximum yields through AI optimization',
        cta: 'Discover Energieglück+'
      },
      landowners: {
        title: 'For Land Owners',
        subtitle: 'Long-term lease income without own investment',
        feature1: '20+ years guaranteed lease income',
        feature2: 'No costs or risks for you',
        feature3: 'We handle development, construction & operation',
        feature4: 'Contribution to regional energy transition',
        cta: 'Learn More'
      },
      municipalities: {
        title: 'For Municipalities',
        subtitle: 'Trade tax, climate goals & future-proof energy location',
        feature1: 'High trade tax revenue (90% stays local)',
        feature2: 'Achieve climate goals & take leadership role',
        feature3: 'Basis for e-mobility & heating networks',
        feature4: '100% private investment – no municipal risk',
        cta: 'Learn More'
      },
      companies: {
        title: 'For Companies',
        subtitle: 'AI solutions that relieve your team and delight customers',
        feature1: 'Reachy — AI employee for all channels, 24/7',
        feature2: '10+ ready-to-use AI use cases',
        feature3: '60-90% faster customer processing',
        feature4: 'GDPR-compliant, Made in Germany',
        cta: 'Discover Use Cases'
      },
      group: {
        title: 'The Energieglück Group',
        subtitle: 'Two companies, one mission: Shaping the energy transition intelligently.',
        bess: {
          name: 'Battery Storage',
          legal: 'Energieglück Batteriespeicher GmbH',
          feature1: 'GridStable — Grid Stabilization & Ancillary Services',
          feature2: 'Co-Location & Substations',
          feature3: '2GW+ Development Pipeline',
          feature4: 'Operation & Asset Management',
          cta: 'Discover GridStable'
        },
        ai: {
          name: 'Artificial Intelligence',
          legal: 'Energieglück AI GmbH',
          feature1: 'Energieglück+ — AI Asset Management',
          feature2: 'AI Agents for Business Processes',
          feature3: 'AI Consulting & Digitalization',
          feature4: 'AI Strategy Consulting',
          cta: 'Discover AI'
        },
        claim: 'We build the storage. And the intelligence behind it.',
        trust: {
          title: 'Trusted by partners across the energy sector',
          text: 'incl. Stadtwerke Göttingen AG'
        }
      },
      closing: 'As project developers, we realize battery storage projects together with strong partners from planning to operation. With Energieglück+, we additionally offer an AI-based asset management platform — autonomous AI agents on a digital twin of your plant.',
      tech: {
        title: 'Innovation Through Technology',
        gridstable: {
          title: 'GridStable - Intelligent Grid Stabilization',
          feature1: 'Frequency stability through BESS',
          feature2: 'Voltage control',
          feature3: 'System services',
          feature4: 'Blackstart capability'
        },
        energieglueck: {
          title: 'Energieglück+ - AI Asset Management',
          feature1: 'Digital twin for energy plants',
          feature2: 'AI-driven process automation',
          feature3: 'Real-time monitoring & forecasting',
          feature4: 'Predictive maintenance'
        },
        reachy: {
          title: 'Reachy - AI Assistant for Companies',
          feature1: 'Automated customer communication (email, chat, phone)',
          feature2: 'Multi-agent teams for complex tasks',
          feature3: 'Proactive work — identifies action needs independently',
          feature4: 'GDPR-compliant, Made in Germany'
        },
        cta1: 'Discover GridStable',
        cta2: 'Get to know Energieglück+',
        cta3: 'Meet Reachy'
      },
      cta: {
        title: 'Become Part of the Energy Transition',
        subtitle: "Let's shape the energy future together",
        offer: {
          title: 'Offer Land',
          text: 'Benefit from long-term lease income',
          cta: 'Learn More'
        },
        project: {
          title: 'Discuss Your Project',
          text: 'For utilities, municipalities and project developers — let\'s plan together',
          cta: 'Schedule a Call'
        },
        ki: {
          title: 'Discover AI Solutions',
          text: 'Consulting, software and AI agents for your business',
          cta: 'View AI Solutions'
        }
      }
    },
    ki: {
      meta: {
        title: 'AI for the Energy Transition — Energieglück AI GmbH | Energieglück',
        description: 'Energieglück AI GmbH develops intelligent solutions for the energy sector and advises companies on digital transformation.'
      },
      hero: {
        title: 'AI for the Energy Transition — and for Your Business',
        subtitle: 'Energieglück AI GmbH develops intelligent solutions for the energy sector and advises companies on digital transformation.',
        cta: 'Book Free Consultation'
      },
      products: {
        title: 'Our Solutions',
        subtitle: 'From asset management to AI assistants to strategic consulting — we make AI accessible.',
        egplus: {
          title: 'Energieglück+',
          desc: 'AI asset management software with digital twin, predictive maintenance and real-time monitoring for maximum plant performance.'
        },
        reachy: {
          title: 'Reachy',
          desc: 'AI assistant for email, phone and chat — automates customer communication, 24/7, GDPR-compliant, Made in Germany.'
        },
        consulting: {
          title: 'AI Consulting',
          desc: 'From assessment through quick wins to long-term AI strategy. We guide your company step by step.'
        },
        learnMore: 'Learn More'
      },
      steps: {
        title: 'Our Consulting Approach',
        subtitle: 'Four steps to successful AI integration.',
        step1: {
          title: 'Assessment',
          desc: 'Where do you stand? Processes, tools, data sources.'
        },
        step2: {
          title: 'Concept',
          desc: 'Data privacy, IT landscape, identify quick wins.'
        },
        step3: {
          title: 'Implementation',
          desc: 'Step-by-step integration, training, change management.'
        },
        step4: {
          title: 'AI Strategy',
          desc: 'Long-term roadmap, monitoring, continuous improvement.'
        },
        cta: 'Book Free Consultation'
      },
      reference: {
        label: 'Reference',
        text: 'Stadtwerke Göttingen AG relies on our AI solutions.'
      }
    },
    playground: {
      registration: {
        title: 'Request access to the AI Playground',
        subtitle: 'Test our AI services with real scenarios. We will send you a personal access link via email.',
        company: 'Company',
        companyPlaceholder: 'Acme Energy Corp.',
        name: 'Name',
        namePlaceholder: 'John Smith',
        email: 'Email',
        emailPlaceholder: 'john.smith@company.com',
        phone: 'Phone (optional)',
        phoneRequired: 'Phone',
        phonePlaceholder: '+1 234 567890',
        phoneError: 'Please provide your phone number.',
        topicsLabel: 'Which AI topics interest you?',
        topic: {
          textAnalysis: 'Text Analysis',
          documentAI: 'Document AI',
          chatbots: 'Chatbots',
          agenticAI: 'Agentic AI'
        },
        privacyText: 'I agree that my data will be processed to provide the AI Playground and for sales outreach. Details in our privacy policy.',
        privacyRequired: 'Please agree to the data processing terms.',
        fieldRequired: 'This field is required.',
        invalidEmail: 'Please enter a valid email address.',
        submit: 'Request access',
        submitting: 'Sending...',
        submitError: 'An error occurred. Please try again.',
        successTitle: 'Request received',
        successText: 'We have sent you a personal access link via email. Please also check your spam folder.'
      }
    },
    footer: {
      company: 'Company',
      about: 'About Us',
      projects: 'Projects',
      press: 'News & Press',
      career: 'Career',
      solutions: 'Solutions',
      noiseAssessment: 'Noise Assessment',
      targets: 'Target Groups',
      landowners: 'Land Owners',
      operators: 'Grid Operators',
      municipalities: 'Municipalities',
      service: 'Service',
      contact: 'Contact',
      imprint: 'Imprint',
      privacy: 'Privacy',
      platform: 'Energieglück+',
      whitepaper: 'Whitepaper',
      copyright: '© 2026 Energieglück Group'
    },
    landowners: {
      hero: {
        eyebrow: 'For Landowners',
        title: 'Turn your land into long-term, predictable lease income.',
        lede: 'We develop, build and operate grid-scale battery storage in Germany. You lease — we cover the investment, permits and operations.',
        ctaPrimary: 'Get a free site check',
        ctaSecondary: 'See how it works'
      },
      kpi: {
        years: { value: '20+', label: 'Years of lease' },
        hectare: { value: 'from 1,000 m²', label: 'Minimum size' },
        invest: { value: '€0', label: 'Your investment' },
        payouts: { value: 'monthly', label: 'Predictable payouts' }
      },
      intro: {
        title: 'Long-term Partnership',
        description: 'Energieglück secures attractive lease or purchase prices for you – we build on partnerships that last for decades. Sustainably used land creates real added value. Do you have a property and questions about the process or suitability? Contact us!'
      },
      contact: {
        title: 'Direct Contact for Owners',
        formButton: 'Contact Form'
      },
      value: {
        eyebrow: 'What it means for you',
        title: 'One contract. Three clear advantages.',
        card1: {
          title: 'Predictable income',
          desc: 'Fixed, indexed lease payments over the full contract term — independent of electricity prices and market conditions.'
        },
        card2: {
          title: 'No risk, no costs',
          desc: 'We handle planning, permits, construction, insurance and decommissioning. You keep ownership of your land.'
        },
        card3: {
          title: 'Land stays usable',
          desc: 'Storage occupies only a small share. Grazing and fringe use often remain possible — no full sealing as with conventional construction.'
        }
      },
      benefits: {
        battery: {
          title: 'Why Battery Storage?',
          description: 'Our innovative battery storage systems secure grid stability and maximize the expansion of renewable energies. With your land, you actively contribute to reducing electricity costs and ensuring a sustainable energy supply for future generations.'
        },
        energieglueck: {
          title: 'Why Energieglück?',
          description: 'We develop, build, and operate battery storage systems for the Energieglück Group – from planning to long-term operation. With years of experience, we realize sustainable projects on your land.'
        }
      },
      process: {
        eyebrow: 'How it works',
        title: 'Four steps. No upfront effort on your side.',
        step1: {
          title: 'Initial Contact',
          description: 'Reach out via the contact form or by email with a few key details about your land.',
          duration: 'Day 1'
        },
        step2: {
          title: 'Land Assessment',
          description: 'Grid connection, topography, permitting feasibility — typically we get back to you with a solid first assessment within one business day.',
          duration: 'within 24 hours'
        },
        step3: {
          title: 'Individual Offer',
          description: 'You receive a concrete offer for lease or purchase, including conditions and term.',
          duration: 'within days'
        },
        step4: {
          title: 'Contract Signing',
          description: 'Notarised contract, registration of the limited personal easement — then we take over.',
          duration: 'on your timeline'
        }
      },
      cta: {
        title: 'Your land may well be suitable.',
        description: 'Send us location and size — we check free of charge and get back to you quickly.',
        button: 'Get my site checked'
      },
      faq: {
        title: 'Frequently Asked Questions',
        question1: {
          question: 'Which lands are suitable?',
          answer: 'Generally, lands from approximately 1 hectare are suitable. Important factors are a nearby grid connection and flat terrain. We are happy to assess the suitability of your land.'
        },
        question2: {
          question: 'How long does the lease contract run?',
          answer: 'Our lease contracts typically run for 20+ years and offer you long-term, predictable income.'
        },
        question3: {
          question: 'What happens after the contract ends?',
          answer: 'After the contract ends, we can either dismantle the facility or extend the contract – depending on your preference.'
        }
      }
    },
    gridoperators: {
      title: 'Grid Operators',
      intro: {
        title: 'GridStable for a Stable Power Grid',
        text: 'GridStable is our concept for grid-supporting battery storage. Through intelligent control and fast response times, our BESS systems effectively stabilize the power grid.'
      },
      gridstable: {
        title: 'GridStable',
        text: 'GridStable is our concept for grid-supporting battery storage. Through intelligent control and fast response times, our BESS systems effectively stabilize the power grid.',
        feature1: 'Frequency stability through BESS',
        feature2: 'Voltage maintenance in the grid',
        feature3: 'System services (SDL)',
        feature4: 'Blackstart capability for grid restoration'
      },
      benefits: {
        title: 'Benefits for Grid Operators',
        stability: { title: 'Grid Stability', text: 'Balancing fluctuations and ensuring a stable power supply around the clock.' },
        efficiency: { title: 'Efficiency Increase', text: 'Optimized grid utilization and reduction of bottlenecks through intelligent load management.' },
        flexibility: { title: 'Flexibility', text: 'Fast response times and flexible provision of control energy as needed.' }
      },
      integration: {
        title: 'Technical Integration',
        connection: { title: 'Grid Connection', text: 'Seamless integration into existing grid infrastructure with standardized interfaces and protocols. Our systems are compatible with all common grid operator standards.' },
        control: { title: 'Control & Monitoring', text: '24/7 real-time monitoring and control via our Energieglück+ system. Complete transparency over all relevant operating parameters and automated optimization.' },
        compliance: { title: 'Standards & Compliance', text: 'Compliance with all relevant standards and guidelines (VDE-AR-N, TAR, EEG). Certified systems and regular audits guarantee the highest security.' }
      },
      asset: {
        title: 'Asset Management with Energieglück+',
        text: 'Grid operators often have their own PV parks and energy facilities. Our Energieglück+ system offers:',
        feature1: 'Digital twin for PV parks and battery storage',
        feature2: 'Day-ahead forecasting for optimal planning',
        feature3: 'Predictive maintenance to prevent failures',
        feature4: 'Real-time performance optimization',
        feature5: 'Automated anomaly detection',
        button: 'More about Energieglück+'
      },
      projects: {
        title: 'Regional Projects & References',
        description: 'Grid-supporting battery storage to support regional grid stability.',
        button: 'View All Projects',
        construction: 'Under Construction',
        operational: 'Operational',
        inverter: 'Inverter',
        storage: 'Storage'
      },
      whyUs: {
        eyebrow: 'Why Energieglück',
        title: 'More than a project developer.',
        text: 'We are more than just a project developer – we are your long-term partner for grid-supporting battery storage',
        holistic: { title: 'Holistic Approach', text: 'Development, construction, and operation from one source – from initial planning to long-term facility operation.' },
        pipeline: { title: '2GW+ Pipeline', text: 'Strong project pipeline with over 2 GW in development for continuous grid stabilization.' },
        partnership: { title: 'Long-term Partnership', text: 'We operate our storage systems ourselves and are your reliable contact for decades.' },
        technology: { title: 'Innovative Technology', text: 'GridStable for grid-supporting control and Energieglück+ for intelligent asset management.' }
      },
      hero: {
        eyebrow: 'For transmission and distribution operators',
        title: 'Grid-supporting storage that actually relieves load.',
        lede: 'We develop, build, and operate battery storage systems that stabilize frequency and voltage — and are blackstart-capable when it matters.',
        ctaPrimary: 'Request project call',
        ctaSecondary: 'How we work'
      },
      kpi: {
        pipeline: { value: '2 GW+', label: 'Project pipeline' },
        realtime: { value: '24/7', label: 'Real-time control' },
        compliance: { value: 'VDE-AR-N', label: 'Compliant & certified' },
        blackstart: { value: '100%', label: 'Blackstart-capable' }
      },
      value: {
        eyebrow: 'What we deliver',
        title: 'Three building blocks for a more stable grid.',
        card1: {
          title: 'Grid-supporting control',
          desc: 'Our BESS systems respond in milliseconds and support frequency and voltage — even in critical load windows.',
          bullet1: 'Frequency stability (FCR, aFRR, mFRR)',
          bullet2: 'Voltage support & reactive power',
          bullet3: 'Blackstart for grid restoration'
        },
        card2: {
          title: 'Seamless integration',
          desc: 'Standardized interfaces and protocols — compatible with all common grid operator standards.',
          bullet1: 'VDE-AR-N, TAR and EEG compliant',
          bullet2: 'Direct connection to control centers',
          bullet3: 'Certified systems, regular audits'
        },
        card3: {
          title: 'Asset management with Energieglück+',
          desc: 'Digital twin, day-ahead forecasting, and predictive maintenance — also for your own PV parks.',
          bullet1: 'Digital twin for PV & BESS',
          bullet2: 'Day-ahead forecasting',
          bullet3: 'Predictive maintenance & anomaly detection'
        }
      },
      approach: {
        eyebrow: 'How we work',
        title: 'GridStable: market service and grid service combined.',
        description: 'Instead of running batteries purely for profit, our algorithms identify critical bottleneck windows and position storage exactly when the grid needs relief — with minimal revenue impact.',
        bullet1: 'Historical pattern recognition of critical bottleneck hours',
        bullet2: 'Proactive SOC positioning before foreseeable load windows',
        bullet3: 'Dual-purpose optimization: market + grid service',
        bullet4: 'Transparent reporting layer for grid operators',
        cta: 'GridStable in detail'
      },
      cta: {
        title: 'Concrete bottleneck in your grid area?',
        lede: 'Let\'s talk about your load flows, your weak points, and how storage can provide relief exactly where you need it.',
        button: 'Request project call',
        text: 'Contact us for more information about our battery storage projects'
      }
    },
    municipalities: {
      title: 'Municipalities',
      hero: {
        eyebrow: 'For mayors and municipal decision-makers',
        title: 'Energy hub of the future. Without own investment.',
        lede: '90% of trade tax stays in your municipality — fully privately financed, with predictable revenue over 15+ years.',
        credibility: 'A BESS pioneer since 2023 with our own on-site storage (partner project, commissioning 2026) — and developer of Germany\'s largest storage by project size.',
        ctaPrimary: 'Schedule consultation',
        ctaSecondary: 'How we work'
      },
      kpi: {
        tax: { value: '90%', label: 'Trade tax local' },
        invest: { value: '€0', label: 'Own investment' },
        duration: { value: '15+ yrs', label: 'Predictable term' },
        private: { value: '100%', label: 'Privately financed' }
      },
      intro: {
        title: 'Your Path to a Future-Ready Energy Location',
        text: 'High trade tax revenues, pioneering role in the energy transition, and foundation for e-mobility, heat networks, and green hydrogen',
        cta1: 'Get Consultation Now',
        cta2: 'View Reference Projects'
      },
      benefits: {
        eyebrow: 'What\'s in it for you',
        title: 'Three measurable benefits for your municipality.',
        subtitle: 'Financial, strategic, and future-oriented — without municipal risk.',
        tax: {
          title: 'High Long-term Trade Tax Revenue',
          highlight: '90% remain directly in your municipality',
          text: 'since the Annual Tax Law 2024 – without risk, without own investment.',
          feature1: 'Predictable income over the entire operating period (15+ years)',
          feature2: '100% private investment – no municipal costs',
          feature3: 'Local value creation through regional contracts'
        },
        pioneer: {
          title: 'Pioneering Role in Energy Transition',
          highlight: 'flagship project',
          text: 'and achieve climate neutrality by 2030/2045 — delivered with Energieglück, the BESS pioneer since 2023.',
          feature1: 'Verified CO₂ savings',
          feature2: 'International reputation as energy pioneer',
          feature3: 'Attractive for future-oriented business location'
        },
        location: {
          title: 'Future-Ready Energy Location',
          highlight: 'infrastructure foundation',
          text: 'for e-mobility, heat networks, and green hydrogen.',
          feature1: 'Charging infrastructure with local green electricity',
          feature2: 'Decarbonized heat supply possible',
          feature3: 'Hydrogen production for industry & mobility'
        }
      },
      energyHub: {
        eyebrow: 'The key concept',
        title: 'More than a facility — an energy hub.',
        subtitle: 'The storage system is the infrastructure foundation for e-mobility, heat networks, and green hydrogen.',
        emobility: {
          title: 'E-Mobility & Charging Infrastructure',
          text: 'Charging parks use locally stored green electricity. Fast charging hubs only become economical with storage.',
          feature1: 'Grid-supporting charging optimization',
          feature2: 'No grid expansion required',
          feature3: 'Reduction of grid fees'
        },
        heating: {
          title: 'Heat Supply & Sector Coupling',
          text: 'Large heat pumps use storage electricity for decarbonized heat supply for districts.',
          feature1: 'CO₂-free heat supply',
          feature2: 'District heating networks for neighborhoods',
          feature3: 'Alternative to gas and oil'
        },
        hydrogen: {
          title: 'Hydrogen Infrastructure',
          text: 'Green hydrogen through electrolysis with storage electricity for industry and mobility.',
          feature1: 'Industrial applications',
          feature2: 'H₂ refueling stations for buses & trucks',
          feature3: 'Long-term storage possible'
        },
        strategic: {
          title: 'Strategic Advantages of the Energy Location',
          efficiency: { title: 'Efficient Land Use', text: 'Multiple energy functions at one location' },
          approval: { title: 'Reduced Approval Effort', text: 'Central infrastructure instead of individual projects' },
          value: { title: 'Maximized Value Creation', text: 'Per square meter and per euro investment' },
          future: { title: 'Future Security', text: 'Flexible expandability without new investments' }
        }
      },
      whyUs: {
        eyebrow: 'Why Energieglück',
        title: 'A partner who understands your municipality.',
        subtitle: 'We bring expertise, innovation, and many years of experience for your municipal energy project',
        holistic: { title: 'Holistic Approach', text: 'Not just storage – we think about the entire energy location concept with e-mobility, heat, and hydrogen' },
        pipeline: { title: '2 GW+ Pipeline', text: 'Extensive project experience and proven processes for reliable implementation' },
        partnership: { title: 'Long-term Partnership', text: 'We accompany you throughout the entire project duration – from planning to operation over 15+ years' },
        technology: { title: 'Innovative Technology', text: 'AI-supported development with Energieglück+ for optimal site selection and profitability' }
      },
      approach: {
        eyebrow: 'How we work',
        title: 'Partnership at eye level.',
        description: 'We see ourselves as a long-term partner for your municipality. Transparency, dialogue, and trustful collaboration are the foundation of our projects.',
        bullet1: 'Open communication with council and administration',
        bullet2: 'Transparent project presentation for all stakeholders',
        bullet3: 'Involvement of local stakeholders from the start',
        bullet4: 'Long-term support throughout the entire project duration (15+ years)'
      },
      projects: {
        title: 'Our Reference Projects',
        subtitle: 'We realize battery storage projects throughout Germany for a sustainable energy future'
      },
      faq: {
        eyebrow: 'FAQ',
        title: 'What municipalities often ask us.',
        q1: 'We have no suitable site — what now?',
        a1: 'We assess it together: AI-assisted site evaluation and years of scouting expertise are part of our process.',
        q2: 'Does the municipality bear any risk or cost?',
        a2: '100% privately financed, €0 own investment — no municipal risk, predictable revenue over 15+ years.',
        q3: 'How certain is delivery?',
        a3: 'Energieglück has been a BESS pioneer since 2023 with a 2 GW+ pipeline and proven processes from scouting to operation.',
        q4: 'How does collaboration with the council and administration work?',
        a4: 'Transparent dialogue and local involvement from the start — we accompany you over the entire project lifetime.'
      },
      cta: {
        title: 'Ready for the Energy Transition?',
        text: 'Let us together make your municipality a future-ready energy location',
        button: 'Schedule Consultation Now'
      }
    },
    gridstable: {
      hero: {
        badge: 'Intelligent Grid Control',
        subtitle: 'Grid-supporting battery storage through intelligent control',
        description: 'GridStable transforms battery storage from pure profit maximizers to active grid helpers. Through intelligent algorithms, we analyze historical grid data, identify critical bottlenecks, and control batteries to provide relief exactly when the grid needs it – while simultaneously optimizing market revenues.'
      },
      features: {
        title: 'What Does GridStable Do?',
        analysis: {
          title: 'Grid Analysis',
          subtitle: 'Understanding the nervous system',
          point1: '<strong>Historical pattern recognition:</strong> We process years of 15-minute measurement data and recognize when and where bottlenecks occur',
          point2: '<strong>Temporal patterns:</strong> Mondays at 6 PM? Fridays with sunshine? Winter peak load? We find the regularities',
          point3: '<strong>Critical windows:</strong> The system identifies the 200-300 most important hours per year where grid relief creates the greatest value',
          point4: '<strong>Cause analysis:</strong> Which factors drive bottlenecks? Weather, industrial large consumers, PV feed-in?'
        },
        algorithms: {
          title: 'Intelligent Schedule Algorithms',
          subtitle: 'Dual-Purpose Optimization',
          forGrid: {
            title: 'For the grid:',
            point1: 'Proactive positioning for foreseeable bottlenecks',
            point2: 'Grid-friendly restrictions in critical moments',
            point3: 'Optimized SOC management'
          },
          forMarket: {
            title: 'For the market:',
            point1: 'Participation in control energy markets (FCR, aFRR, mFRR)',
            point2: 'Capturing short-term intraday trading opportunities',
            point3: 'Machine learning price forecasts (24-48h)'
          }
        },
        integration: {
          title: 'Seamless Integration',
          subtitle: 'Industry standards',
          description: 'GridStable speaks the language of your existing infrastructure:',
          point1: '<strong>IEC 61850 & IEC 60870-5-104:</strong> Standard protocols for grid operator communication',
          point2: '<strong>Modbus:</strong> For local devices (battery system, inverter, sensors)',
          point3: '<strong>SCADA gateways:</strong> Hardware routers connect cloud optimization with OT infrastructure',
          point4: '<strong>VDE-AR-N 4110 compliant:</strong> Voltage maintenance, frequency control, reactive power provision'
        },
        monitoring: {
          title: 'Transparent Monitoring',
          subtitle: 'Trust through data',
          description: 'A real-time dashboard continuously shows grid operators:',
          point1: 'How much grid relief we are currently providing',
          point2: 'What restrictions we are currently implementing',
          point3: 'Historical performance against agreed KPIs',
          point4: 'Automated compliance reports'
        }
      },
      benefits: {
        title: 'The Added Value for Grid Operators',
        subtitle: 'Concrete Benefits',
        point1: '10-20% reduction of redispatch costs in critical bottleneck regions',
        point2: 'Delay or avoidance of expensive grid expansion investments through intelligent load distribution',
        point3: 'Safe expansion of renewable energies without stability risks',
        point4: 'Better forecast and predictability of storage behavior in your grid',
        point5: 'Transparent real-time monitoring of all grid-supporting measures'
      },
      comparison: {
        title: 'Why GridStable is Different',
        aspect: 'Aspect',
        conventional: 'Conventional Storage',
        gridstable: 'GridStable',
        row1: {
          aspect: 'Focus',
          conventional: 'Profit maximization',
          gridstable: '✓ Profit + grid stability'
        },
        row2: {
          aspect: 'Planning',
          conventional: 'Daily',
          gridstable: '✓ 24-48h forward-looking'
        },
        row3: {
          aspect: 'Grid knowledge',
          conventional: 'Rudimentary',
          gridstable: '✓ Data-driven bottleneck analysis'
        },
        row4: {
          aspect: 'Integration',
          conventional: 'Manual SCADA connection',
          gridstable: '✓ Automated, industry standard'
        },
        row5: {
          aspect: 'Transparency',
          conventional: 'Black box',
          gridstable: '✓ Real-time dashboard for grid operators'
        }
      },
      cta: {
        title: 'Interested in GridStable?',
        description: "Let's discuss how GridStable can stabilize your grid",
        button: 'Contact Us Now'
      }
    },
    gridSimulator: {
      title: 'GridStable Grid Load Simulator',
      subtitle: 'How Battery Storage Stabilizes the Power Grid',
      chart: {
        yAxis: 'Grid Utilization (%)',
        criticalLimit: 'Critical Limit (75%)',
        without: 'Without GridStable',
        with: 'With GridStable'
      },
      tooltip: {
        time: '',
        without: 'Without GridStable',
        with: 'With GridStable'
      },
      slider: {
        label: 'Storage Size'
      },
      metrics: {
        peakReduction: {
          label: 'Peak Load Reduction'
        },
        criticalHours: {
          label: 'Critical Hours',
          description: 'Saved per day'
        },
        redispatchSavings: {
          label: 'Redispatch Savings',
          description: 'Per year'
        },
        marketRevenue: {
          label: 'Market Revenue',
          description: 'Through optimization'
        }
      },
      info: '<strong>Scenario:</strong> Average summer day with PV feed-in in a regional grid with 100 MW peak load. <strong>Methodology:</strong> Our own scientific simulations and calculations based on BDEW load profiles, SMARD grid data, and current load studies.'
    },
    about: {
      hero: {
        headline: 'Energy. Reimagined. Built for generations.',
        subtitle: 'We combine 17 years of energy industry expertise with cutting-edge AI and the stability of a family foundation.'
      },
      intro: {
        title: 'About Energieglück',
        text: 'Energieglück operates in two areas: BESS and AI. We develop and operate battery storage systems with a pipeline of more than 2 GW; our first project in Völklingen goes live in 2026. With Energieglück+, AI agents and targeted AI consulting, we also support companies with asset management and digitalization.'
      },
      story: {
        text: 'In 2009, Dr. Nico Haberer landed in the energy industry almost by chance — but with a firm intention to change things. Starting as an SAP consultant, he built a consulting firm with 70 employees over more than a decade together with partners. But when he started training models with mass data and saw: it works — he realized that AI is the future. The ChatGPT moment confirmed this. His partners didn\'t follow this vision. So he broke away and founded Energieglück.',
        quote: 'Be the first to try new innovation — and if it makes sense, integrate it everywhere. Professionally and personally.',
        closing: 'The idea for battery storage came from a property owned by the family foundation — and the realization that real value for the next generation doesn\'t lie in consulting contracts, but in infrastructure and technology.'
      },
      team: {
        title: 'The people behind Energieglück',
        p0: { name: 'Kathy Ensch', role: 'Managing Director Energieglück Batteriespeicher GmbH & Energieglück AI GmbH', desc: 'The link between technology and customer.' },
        p1: { name: 'Dr. Nico Haberer', role: 'Founder Energieglück Group, Managing Director Energieglück Batteriespeicher GmbH', desc: '17 years in energy, 7 years in AI. From vision to product.' }
      },
      companies: {
        title: 'Two companies, one goal',
        bess: { name: 'Energieglück Batteriespeicher GmbH', desc: 'BESS development, co-location, substations' },
        ai: { name: 'Energieglück AI GmbH', desc: 'Energieglück+, GridStable, AI agents' }
      },
      structure: {
        title: 'Built for the long term',
        foundation: 'Haberer Family Foundation',
        holding: 'Energieglück GmbH (Holding)',
        bess: 'EG Batteriespeicher GmbH',
        ai: 'EG AI GmbH',
        spvs: 'Project SPVs',
        claim: 'Independent. Long-term. Generational thinking instead of quarterly thinking.'
      },
      reference: {
        quote: 'Energieglück+ enables us to manage our assets intelligently and efficiently — and communication is always transparent, solution-oriented, and collaborative.',
        author: 'Bernd Schill',
        company: 'Head of Renewables, Stadtwerke Göttingen AG'
      },
      mission: {
        title: 'Our Mission',
        text: 'We build battery storage systems and develop AI solutions that make the energy transition smarter. Infrastructure and software from a single source — for stable grids, optimized processes and a better future for our children.'
      },
      vision: {
        title: 'Our Vision',
        text: 'Storage and AI become a natural part of a reliable, clean energy system. Not as a promise for someday, but as technology that works today — leaving the world better for future generations.'
      },
      values: {
        title: 'Our Values',
        kiFirst: { title: 'AI-First', desc: 'Every decision, every process — data-driven and AI-powered.' },
        precision: { title: 'Precision', desc: 'Decisions based on robust models, not gut feeling.' },
        automation: { title: 'Automation', desc: 'What can be automated, will be automated.' },
        sustainability: { title: 'Sustainability', desc: 'Economic viability and energy transition — always together.' }
      }
    },
    projects: {
      title: 'Our Projects',
      subtitle: 'Battery storage projects from development to operation',
      featured: {
        badge: 'Reference Project',
        power: 'Power',
        capacity: 'Capacity',
        inverter: 'Inverter',
        battery: 'Battery Storage',
        statusLabel: 'Status',
        statusValue: 'Commissioning planned end of 2026',
        description: 'Managed by Energieglück+ — our AI asset management in action.'
      },
      pipeline: {
        title: 'Selection from our Pipeline',
        subtitle: 'Additional locations in planning and development',
        colLocation: 'Location',
        colPower: 'Power',
        colCapacity: 'Capacity',
        colStatus: 'Status',
        statusDev: 'In Development',
        badgeLarge: 'Large-scale',
        nrw: 'North Rhine-Westphalia',
        rlp: 'Rhineland-Palatinate',
        sachsen1: 'Saxony',
        sachsen2: 'Saxony',
        sachsenAnhalt1: 'Saxony-Anhalt',
        sachsenAnhalt2: 'Saxony-Anhalt',
        thueringen: 'Thuringia'
      }
    },
    careers: {
      meta: {
        title: 'Careers - Energieglück',
        description: 'Shape the energy of the future with us. Join the Energieglück team and work on battery storage, GridStable software, and the Energieglück+ platform.',
      },
      hero: {
        welcome: 'Welcome to Energieglück',
        title: 'Shape the energy of the future with us',
        description: 'At Energieglück, we work every day to make the energy transition a reality. With our innovative battery storage systems, intelligent GridStable software, and the Energieglück+ platform, we create holistic solutions for a sustainable energy future. We are looking for dedicated talents who share our passion for renewable energy and want to shape the future with us.',
        imageAlt: 'Team at Energieglück',
      },
      benefits: {
        title: 'Why Energieglück?',
        subtitle: 'We offer more than just a job – a meaningful work environment with future prospects',
        meaningful: {
          title: 'Meaningful Work',
          description: 'Actively contribute to the energy transition and shape a sustainable future',
        },
        innovation: {
          title: 'Innovation',
          description: 'Work with cutting-edge technology and AI-powered systems',
        },
        team: {
          title: 'Strong Team',
          description: 'Become part of a motivated team with flat hierarchies',
        },
      },
      jobs: {
        title: 'Current Job Openings',
        subtitle: 'Discover our open positions and become part of the energy transition',
      },
      application: {
        title: 'Speculative Application',
        description: 'No suitable position? Convince us with your speculative application',
        button: 'Send Speculative Application',
        emailSubject: 'Speculative Application',
      },
      contact: {
        text: 'Questions about your application? Contact us at',
      },
    },
    press: {
      eyebrow: 'Newsroom',
      title: 'News & Press',
      subtitle: 'Press releases, project updates and background stories from Energieglück.',
      source: 'Source:',
      original: 'Original Article',
      empty: 'Currently no press releases are available.',
      readMore: 'Read more',
      backToOverview: 'Back to News & Press',
      notFound: 'Article not found',
      publishedAt: 'Published at:',
      filter: {
        all: 'All',
      },
      contact: {
        title: 'Press Contact',
        text: 'We are happy to assist with press inquiries',
        name: 'Marny Meyer',
        email: 'presse@energieglueck.de'
      },
      categories: {
        unternehmen: 'Company',
        projekt: 'Project',
        technologie: 'Technology'
      }
    },
    career: {
      meta: {
        title: 'Career - Energieglück',
        description: 'Shape the energy of the future with us. Become part of the Energieglück team and work on battery storage, GridStable software, and the Energieglück+ platform.'
      },
      hero: {
        badge: 'Welcome to Energieglück',
        title: 'Shape the Energy of the Future with Us',
        text: 'At Energieglück, we work every day to make the energy transition a reality. With our innovative battery storage systems, intelligent GridStable software, and the Energieglück+ platform, we create comprehensive solutions for a sustainable energy future. We are looking for committed talents who share our passion for renewable energies and want to shape the future with us.'
      },
      why: {
        title: 'Why Energieglück?',
        subtitle: 'With us, you can expect more than just a job – a meaningful work environment with future prospects',
        meaningful: { title: 'Meaningful Work', text: 'Actively contribute to the energy transition and shape a sustainable future' },
        innovation: { title: 'Innovation', text: 'Work with state-of-the-art technology and AI-supported systems' },
        team: { title: 'Strong Team', text: 'Become part of a motivated team with flat hierarchies' }
      },
      jobs: {
        title: 'Current Job Openings',
        subtitle: 'Discover our open positions and become part of the energy transition',
        initiative: {
          title: 'Speculative Application',
          text: 'No suitable position available? Convince us with your speculative application',
          button: 'Send Speculative Application'
        },
        questions: 'Questions about the application? Write to us at'
      }
    },
    contact: {
      title: 'Contact',
      subtitle: 'Do you have questions? We are here for you!',
      form: {
        title: 'Contact Form',
        firstname: 'First Name',
        lastname: 'Last Name',
        email: 'Email',
        phone: 'Phone',
        subject: 'Subject',
        message: 'Message',
        placeholder: 'Your message to us...',
        submit: 'Submit',
        submitting: 'Sending...',
        selectSubject: 'Please select...',
        subjects: {
          landowner: 'Land Owner',
          gridoperator: 'Grid Operator',
          municipality: 'Municipality',
          demo: 'Energieglück+ Demo',
          press: 'Press',
          other: 'Other'
        },
        success: {
          title: 'Message sent!',
          text: 'Thank you for your message! We will get back to you as soon as possible.'
        },
        error: {
          title: 'Error sending',
          text: 'Your message could not be sent. Please try again later.'
        }
      },
      info: {
        title: 'Contact Information',
        company: 'Energieglück Group',
        demo: {
          title: 'Interested in Energieglück+?',
          text: 'Experience our AI-supported asset management software in a personal demo',
          button: 'Schedule Demo Now'
        }
      }
    },
    imprint: {
      meta: {
        title: 'Imprint | Energieglück Group',
        description: 'Imprint of the Energieglück Group - Legal information and contact details of the company.'
      },
      title: 'Imprint',
      company: {
        title: 'Information according to § 5 DDG (formerly § 5 TMG)',
        name: 'Energieglück Group'
      },
      representative: {
        title: 'Represented by',
        name: 'Dr. Nico Haberer'
      },
      register: {
        title: 'Register Entry',
        entry: 'Entry in the commercial register.',
        court: 'Registry court: District Court Saarbrücken',
        number: 'Registration number: HRB 111679'
      },
      contact: {
        title: 'Contact',
        email: 'Email: info@energieglueck.de'
      },
      responsible: {
        title: 'Responsible for content according to § 18 para. 2 MStV',
        name: 'Dr. Nico Haberer'
      },
      dispute: {
        title: 'Consumer dispute resolution / Universal arbitration board',
        text: 'We do not participate in dispute resolution proceedings before a consumer arbitration board and are not obligated to do so.'
      },
      liability: {
        title: 'Disclaimer',
        content: {
          title: 'Liability for content',
          text: 'The contents of our pages were created with the greatest care. However, we cannot guarantee the accuracy, completeness, and timeliness of the content. As a service provider, we are responsible for our own content on these pages according to general laws in accordance with § 7 para. 1 DDG. According to §§ 8 to 10 DDG, however, we as a service provider are not obligated to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity. Obligations to remove or block the use of information according to general laws remain unaffected. However, liability in this regard is only possible from the point in time at which knowledge of a specific legal violation is obtained. Upon becoming aware of corresponding legal violations, we will remove this content immediately.'
        },
        links: {
          title: 'Liability for links',
          text: 'Our offer contains links to external websites of third parties, on whose contents we have no influence. Therefore, we cannot assume any liability for these external contents. The respective provider or operator of the pages is always responsible for the contents of the linked pages. The linked pages were checked for possible legal violations at the time of linking. Illegal contents were not recognizable at the time of linking. However, a permanent content control of the linked pages is not reasonable without concrete evidence of a legal violation. Upon becoming aware of legal violations, we will remove such links immediately.'
        },
        copyright: {
          title: 'Copyright',
          text: 'The contents and works created by the site operators on these pages are subject to German copyright law. The reproduction, editing, distribution, and any kind of exploitation outside the limits of copyright require the written consent of the respective author or creator. Downloads and copies of this page are only permitted for private, non-commercial use. Insofar as the content on this page was not created by the operator, the copyrights of third parties are respected. In particular, third-party content is marked as such. Should you nevertheless become aware of a copyright infringement, we ask for a corresponding note. Upon becoming aware of legal violations, we will remove such content immediately.'
        }
      },
      privacy: {
        title: 'Privacy',
        gdpr: 'Privacy policy according to GDPR',
        intro: 'We are very pleased about your interest in our company. Data protection has a particularly high priority for the management of the Energieglück Group.',
        responsible: {
          title: 'Responsible',
          company: 'Energieglück Group',
          representative: 'Represented by: Dr. Nico Haberer'
        },
        collection: {
          title: 'Collection and processing of personal data',
          intro: 'When visiting our website, the following data is stored:',
          data1: 'IP address',
          data2: 'Date and time of access',
          data3: 'Browser used',
          data4: 'Operating system',
          data5: 'Referrer URL',
          outro: 'This technical data is necessary for the display and security of the website and is stored in server log files.'
        },
        contact: {
          title: 'Contact form & communication',
          text: 'If you send us inquiries via contact form or email, your information will be stored for the purpose of processing the inquiry and for follow-up questions. We do not share this data without your consent.'
        },
        cookies: {
          title: 'Cookies and analysis',
          text: 'Our website uses cookies. See cookie policy for details. Cookies serve better usability and possibly web analysis.'
        },
        rights: {
          title: 'Rights of users',
          text: 'You have the right to information, correction, deletion, restriction of processing, objection, and data portability. Please contact info@energieglueck.de for such concerns at any time.'
        },
        complaint: {
          title: 'Right to lodge a complaint',
          text: 'There is a right to lodge a complaint with the competent data protection supervisory authority, e.g. LfDI Saarland.'
        },
        thirdparty: {
          title: 'Third party services',
          text: 'If Google Analytics, social media plugins, or other external services are used, this must be specified here additionally individually.'
        },
        changes: {
          title: 'Amendment of the privacy policy',
          text: 'This statement will be updated in case of legal changes or adjustments to the website.'
        }
      }
    },
    whitepaper: {
      meta: {
        title: 'Whitepaper | Energieglück',
        description: 'Free analyses, studies and technical reports for energy industry professionals.'
      },
      hero: {
        badge: 'Expert Knowledge',
        title: 'Analyses. Studies. Expert Knowledge.',
        subtitle: 'Well-founded decision-making resources for energy professionals — free of charge.'
      },
      authors: 'Authors',
      publisher: 'Publisher: Dr. Nico Haberer, Energieglück Group',
      downloadFree: 'Download for free',
      notifyMe: 'Register interest',
      coverLabel: 'Whitepaper',
      comingSoonBadge: 'Coming Soon',
      pages: 'Pages',
      readingTime: 'min read',
      upcoming: {
        title: 'Coming Soon',
        subtitle: 'These studies are currently in progress.'
      },
      cta: {
        title: 'Need a custom analysis for your project?',
        subtitle: 'Our experts are happy to advise you on BESS, hybrid energy parks and intelligent asset management.',
        button: 'Request consultation'
      }
    }
  }
};
